import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import BranchBroadcasts from '@/components/BranchBroadcasts';
import { Footer } from '@/components/organisms/Footer';
import { branchTypeTheme } from '@/utils/branchTypeTheme';
import BookingChat from '@/components/BookingChat/BookingChat';
import TableGroupChat from '@/components/TableGroupChat';
import CallStaff from '@/components/CallStaff/CallStaff';
import BillModal from '@/components/TableBill';
import TableServiceOrderModal from '@/components/TableServiceOrder/TableServiceOrderModal';
import GameScorePanel from '@/components/GameScore/GameScorePanel';
import ShoppingRequestChat from '@/components/ShoppingRequestChat/ShoppingRequestChat';
import LiveSongRequestPanel from '@/components/LiveSongRequest/LiveSongRequestPanel';
import LiveSongRequestList from '@/components/LiveSongRequest/LiveSongRequestList';
import LiveSongAllRequests from '@/components/LiveSongRequest/LiveSongAllRequests';
import {
  getBranchById,
  getTablesByBranchId,
  getOrganizerById,
  MOCK_TABLES,
  Table,
  Branch
} from '@/data/mockTableBookingData';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { tableBookingWebSocketService } from '@/services/tableBookingWebSocket';
import WaitlistJoinButton from '@/components/WaitlistJoin';
import { shoppingRequestWebSocketService } from '@/services/shoppingRequestWebSocket';
import { isValidUUID } from '@/utils/uuid';
import styles from './TableSelection.module.scss';

interface Area {
  id: string;
  branchId: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface AreaWithTables {
  area: Area;
  tables: Table[];
}

interface TableSelectionState {
  branch: Branch | null;
  areas: AreaWithTables[];
  organizer: any;
  selectedTable: Table | null;
  showBookingModal: boolean;
  /** True when the customer already holds an active booking (one-active-booking rule). */
  hasActiveBooking: boolean;
  showChatBox: boolean;
  activeBooking: any | null;
  activeTableChat: Table | null; // Table for which chat is currently open
  activeTableBookingId: string | null; // Actual booking ID for the active table chat (clean UUID)
  showTableGroupChat: boolean; // Table GROUP chat (organizer + all checked-in customers) open
  tableGroupChatBookingId: string | null; // Booking ID for the open group chat
  tableGroupChatTableName: string | null; // Display name for the open group chat
  showCallStaff: boolean; // "Call staff" table-call panel open
  callStaffBookingId: string | null; // Booking ID for the open call panel
  callStaffTableName: string | null; // Display name for the open call panel
  showBill: boolean; // "View bill / Pay" modal open
  billBookingId: string | null; // Booking ID for the open bill modal
  billTableName: string | null; // Display name for the open bill modal
  showOrder: boolean; // "Order / Menu" order sheet open
  orderBookingId: string | null; // Booking ID for the open order sheet
  orderTableName: string | null; // Display name for the open order sheet
  showGameScore: boolean; // "Live score" game scoreboard modal open (game/sports venues)
  gameScoreBookingId: string | null; // Booking ID for the open scoreboard
  gameScoreTableId: string | null; // Table ID for the open scoreboard (Ably channel + session lookup)
  gameScoreTableName: string | null; // Display name for the open scoreboard
  showBookingDetailsModal: boolean;
  selectedTableForDetails: Table | null;
  tableBookings: any[];
  isLoadingBookings: boolean;
  bookingsError: string | null;
  bookingForm: {
    numberOfCustomers: number;
    bookingDate: string;
    startTime: string;
    durationMinutes: number;
    specialRequests: string;
  };
  isLoading: boolean;
  isSubmittingBooking: boolean;
  /** Table id for which an instant "Sit here now" request is in flight (double-tap guard). */
  sittingNowTableId: string | null;
  /** Table id for which an "I'm here — seat me" request is in flight (double-tap guard). */
  imHereTableId: string | null;
  bookingError: string | null;
  bookingSuccess: boolean;
  error: string | null;
  socketNotification: {
    show: boolean;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
  } | null;
  unreadMessageCounts: { [tableId: string]: number }; // Track unread message counts per table
  callUnreadCounts: { [tableId: string]: number }; // Unread "Call staff" chat replies from staff, per table
  /** When set, show booking chat (from FCM or URL ?openChat=bookingId). */
  openChatBookingId: string | null;
  /** Branch type code from API (e.g. "grocery") - when "grocery" show grocery shop view */
  branchTypeCode: string | null;
  /** Resolved feature pillars for THIS branch (branch-type aware). null = not yet loaded.
   *  Used to hide bar-only extras (e.g. Live Song Request) on venues that don't have them. */
  branchFeatures: string[] | null;
  /** Branch ISO currency code (e.g. "INR") for money formatting; undefined falls back to default. */
  branchCurrency: string | null;
  /** Current shopping request for grocery branch (from POST/GET shopping-requests). */
  myShoppingRequest: { id: string; status: string; branchId?: string; branch?: { name?: string } } | null;
  /** When true, show ShoppingRequestChat for myShoppingRequest */
  showShoppingRequestChat: boolean;
  /** Unread message count for shopping request chat (shown on Chat button when chat is closed). */
  shoppingRequestUnreadCount: number;
  /** Grocery branch: stock items grouped by category (from branch details API). */
  stockItemsByCategory: Array<{
    category: { id: string; categoryName: string; categoryImage?: string; displayOrder?: number };
    subCategories: Array<{
      subCategory: { id: string; subCategoryName: string; subCategoryImage?: string };
      items: Array<{
        id: string;
        itemName: string;
        itemPrice: number;
        itemImage?: string;
        itemUnit?: string;
        itemUnitDescription?: string;
        itemPriceWithDetails?: string;
      }>;
    }>;
    itemsWithoutSubCategory?: any[];
  }>;
  /** Shop: search filter (item/category/subcategory name). */
  shopSearchQuery: string;
  /** Shop: visible item count per key (categoryId-subId or categoryId-no-sub). Key -> count. */
  shopVisibleCount: { [key: string]: number };
  /** Shop: selected category id (step 1 → 2). Null = show categories. */
  shopSelectedCategoryId: string | null;
  /** Shop: selected subcategory id (step 2 → 3). Null = show subcategories of selected category. */
  shopSelectedSubCategoryId: string | null;
  /** Shop: cart as stockItemId -> quantity (for share-in-chat cart order). */
  shopCart: Record<string, number>;
  /** Error when sharing cart (e.g. no request, API error). */
  shopCartError: string | null;
  /** True while creating cart order and sending link in chat. */
  isSharingCart: boolean;
  /** Chair assignments from my-seats API: tableId -> bookingId */
  chairAssignments: Record<string, string>;
  /** Pending seat requests: tableId -> seatId (for UI tracking) */
  pendingSeatRequests: Record<string, string>;
  /** Open-for-join bookings cache: tableId -> bookingId */
  openForJoinMap: Record<string, string>;
  /** When true, show Request Seat confirmation modal */
  showRequestSeatModal: boolean;
  /** Table for which seat is being requested */
  requestSeatTable: Table | null;
  /** Loading state for seat request */
  isRequestingSeat: boolean;
  /** Error for seat request */
  requestSeatError: string | null;
  /** Chair picker state for the booking modal */
  chairsStatus: Array<{ chairNumber: number; status: 'available' | 'booked' | 'unavailable' }>;
  isLoadingChairs: boolean;
  selectedChairNumber: number | null;
  /** Join mode: true when customer is joining an existing open booking */
  isJoinMode: boolean;
  /** Full main booking object for join mode (from open-for-join API) */
  joinMainBooking: any | null;
  /** Full open-for-join booking objects keyed by tableId */
  openForJoinBookingDetails: Record<string, any>;
  /** When true, show Live Song Request modal. */
  showLiveSongModal: boolean;
  /** Active tab in Live Song Request modal: 'request' | 'all'. */
  liveSongModalTab: 'request' | 'all';
  /** When true, show Requested Songs list modal. */
  showRequestedSongsModal: boolean;
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    return <WrappedComponent {...props} navigate={navigate} params={params} />;
  };
}

class TableSelection extends Component<any, TableSelectionState> {
  /** Shopping-request statuses that count as still open (block creating another request). */
  static readonly OPEN_SHOPPING_STATUSES = ['requested', 'confirmed', 'active'];

  constructor(props: any) {
    super(props);
    this.state = {
      branch: null,
      areas: [],
      organizer: null,
      selectedTable: null,
      showBookingModal: false,
      hasActiveBooking: false,
      sittingNowTableId: null,
      imHereTableId: null,
      showChatBox: false,
      activeBooking: null,
      activeTableChat: null,
      activeTableBookingId: null,
      showTableGroupChat: false,
      tableGroupChatBookingId: null,
      tableGroupChatTableName: null,
      showCallStaff: false,
      callStaffBookingId: null,
      callStaffTableName: null,
      showBill: false,
      billBookingId: null,
      billTableName: null,
      showOrder: false,
      orderBookingId: null,
      orderTableName: null,
      showGameScore: false,
      gameScoreBookingId: null,
      gameScoreTableId: null,
      gameScoreTableName: null,
      showBookingDetailsModal: false,
      selectedTableForDetails: null,
      tableBookings: [],
      isLoadingBookings: false,
      bookingsError: null,
      bookingForm: {
        numberOfCustomers: 2,
        bookingDate: '',
        startTime: '',
        durationMinutes: 60,
        specialRequests: ''
      },
      isLoading: true,
      isSubmittingBooking: false,
      bookingError: null,
      bookingSuccess: false,
      error: null,
      socketNotification: null,
      unreadMessageCounts: {},
      callUnreadCounts: {},
      openChatBookingId: null,
      branchTypeCode: null,
      branchFeatures: null,
      branchCurrency: null,
      myShoppingRequest: null,
      showShoppingRequestChat: false,
      shoppingRequestUnreadCount: 0,
      stockItemsByCategory: [],
      shopSearchQuery: '',
      shopVisibleCount: {},
      shopSelectedCategoryId: null,
      shopSelectedSubCategoryId: null,
      shopCart: {},
      shopCartError: null,
      isSharingCart: false,
      chairAssignments: {},
      pendingSeatRequests: {},
      openForJoinMap: {},
      showRequestSeatModal: false,
      requestSeatTable: null,
      isRequestingSeat: false,
      requestSeatError: null,
      chairsStatus: [],
      isLoadingChairs: false,
      selectedChairNumber: null,
      isJoinMode: false,
      joinMainBooking: null,
      openForJoinBookingDetails: {},
      showLiveSongModal: false,
      liveSongModalTab: 'request',
      showRequestedSongsModal: false
    };
  }

  /** Ably unsubscribe for live "Call staff" chat replies (staff → this customer). */
  callMsgUnsub: (() => void) | null = null;
  orderStatusUnsub: (() => void) | null = null;

  componentDidMount() {
    const { organizerId, branchId } = this.props.params || {};
    if (organizerId && branchId) {
      this.loadData(organizerId, branchId);
    }
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    // Prefer bookingId from URL (e.g. FCM: .../tables?bookingId=uuid&openChat=1); else use openChat only if it's a valid UUID
    const urlBookingId = params.get('bookingId')?.trim();
    const openChat = params.get('openChat')?.trim();
    const chatBookingId =
      (urlBookingId && isValidUUID(urlBookingId))
        ? urlBookingId
        : (openChat && isValidUUID(openChat) ? openChat : null);
    if (chatBookingId) {
      this.setState({ openChatBookingId: chatBookingId, showChatBox: true });
    }
    this.setupSocketListeners();
    this.checkActiveBooking();
    this.setupCallMessageNotifications();
  }

  /**
   * Live unread badge on the "Call staff" button: staff replied inside a call's chat while the
   * customer's Call-staff panel is closed. Seeds counts already waiting on the server, then keeps
   * them live over the customer's Ably user channel. (Opening the panel marks the thread read.)
   */
  setupCallMessageNotifications = async () => {
    const { accessToken } = apiService.getTokens();
    const myUserId = accessToken ? getUserIdFromToken(accessToken) : null;
    if (!myUserId) return;

    // Seed: any staff replies that arrived before this screen mounted.
    try {
      const res = await apiService.getServiceCalls();
      const calls = ((res as any)?.data || []) as Array<{ tableId?: string | null; unreadCount?: number }>;
      const seed: { [tableId: string]: number } = {};
      for (const c of calls) {
        if (c.tableId && c.unreadCount) seed[c.tableId] = (seed[c.tableId] || 0) + c.unreadCount;
      }
      if (Object.keys(seed).length) {
        this.setState((prev) => ({ callUnreadCounts: { ...seed, ...prev.callUnreadCounts } }));
      }
    } catch { /* non-fatal */ }

    // Live: bump when staff sends a message and this table's Call-staff panel isn't open.
    this.callMsgUnsub = subscribeToChannel(
      `user:${myUserId}`,
      ['service_ticket_message:new'] as unknown as readonly string[],
      (_event, data: any) => {
        if (!data?.tableId || data.senderType !== 'organizer') return;
        const panelOpen = this.state.showCallStaff && this.state.callStaffBookingId === data.bookingId;
        if (panelOpen) return; // the customer is looking at the thread
        this.setState((prev) => ({
          callUnreadCounts: {
            ...prev.callUnreadCounts,
            [data.tableId]: (prev.callUnreadCounts[data.tableId] || 0) + 1,
          },
        }));
      }
    );

    // Live: order status changes (approve / reject / preparing / on the way / delivered) → toast,
    // even when the order panel is closed. The order itself updates inside the modal when open.
    this.orderStatusUnsub = subscribeToChannel(
      `user:${myUserId}`,
      ['service_order:updated'] as unknown as readonly string[],
      (_event, data: any) => {
        if (!data?.id || !data.status) return;
        const label = data.tableLabel || 'your table';
        const map: Record<string, { type: string; title: string; message: string }> = {
          approved: { type: 'success', title: '✅ Order approved', message: `Your order at ${label} was approved — it’s being prepared.` },
          making: { type: 'info', title: '👨‍🍳 Preparing your order', message: `The kitchen is preparing your order at ${label}.` },
          coming: { type: 'info', title: '🛎️ On the way', message: `Your order at ${label} is on the way.` },
          delivered: { type: 'success', title: '🍽️ Delivered', message: `Your order at ${label} has been delivered. Enjoy!` },
          rejected: { type: 'error', title: '❌ Order declined', message: `Your order at ${label} was declined. Please check with staff.` },
        };
        const n = map[data.status];
        if (!n) return;
        this.setState({ socketNotification: { show: true, ...n } as any });
        setTimeout(() => this.setState({ socketNotification: null }), 6000);
      }
    );
  };

  /**
   * Determine whether the customer already holds an active booking (one-active-booking
   * rule). Grocery "Main Table" shopping-requests are excluded — they are a separate domain.
   */
  checkActiveBooking = async () => {
    try {
      const branchId = this.props.params?.branchId;
      const res = await apiService.getMyBookings();
      if (res.success && res.data?.bookings) {
        // One active booking per VENUE (branch), not globally — a customer can be seated at a bar
        // and a salon at the same time. Only bookings at THIS branch block a new one here.
        const active = res.data.bookings.some((b: any) =>
          ['requested', 'pending', 'confirmed', 'active'].includes(b.status) &&
          b.table?.tableName !== 'Main Table' &&
          (b.branchId === branchId || b.branch?.id === branchId || b.table?.branchId === branchId)
        );
        this.setState({ hasActiveBooking: active });
      }
    } catch (err) {
      // Non-fatal: the server still enforces the rule on booking.
      console.warn('[TableSelection] checkActiveBooking failed', err);
    }
  };

  componentWillUnmount() {
    this.removeSocketListeners();
    shoppingRequestWebSocketService.disconnect();
    try { this.callMsgUnsub?.(); } catch { /* noop */ }
    try { this.orderStatusUnsub?.(); } catch { /* noop */ }
  }

  setupSocketListeners = () => {
    tableBookingWebSocketService.connect();

    const branchId = this.props.params?.branchId;
    if (branchId) {
      tableBookingWebSocketService.connectBranch(branchId);
    }

    tableBookingWebSocketService.on('table_booking:approved', this.handleBookingApproved);
    tableBookingWebSocketService.on('table_booking:rejected', this.handleBookingRejected);
    tableBookingWebSocketService.on('table_booking:cancelled', this.handleBookingCancelled);
    tableBookingWebSocketService.on('table_booking:checked_in', this.handleCheckedIn);
    tableBookingWebSocketService.on('table_booking:completed', this.handleCompleted);
    tableBookingWebSocketService.on('table_booking:is_table_yours_updated', this.handleIsTableYoursUpdated);
    tableBookingWebSocketService.on('table_booking:is_booked_by_you_updated', this.handleIsBookedByYouUpdated);
    tableBookingWebSocketService.on('booking_message:notification', this.handleChatNotification);
    tableBookingWebSocketService.on('table:booking_opened_updated', this.handleBookingOpenedUpdated);
    tableBookingWebSocketService.on('booking_seat:requested', this.handleSeatRequested);
    tableBookingWebSocketService.on('booking_seat:assigned', this.handleSeatAssigned);
    tableBookingWebSocketService.on('table_booking:checked_out', this.handleTableCheckedOut);
    tableBookingWebSocketService.on('table:status_updated', this.handleTableStatusUpdated);
    tableBookingWebSocketService.on('booking_seat:removed', this.handleSeatRemoved);
    tableBookingWebSocketService.on('error', this.handleSocketError);
    tableBookingWebSocketService.on('connect', this.handleReconnectResync);
  };

  removeSocketListeners = () => {
    tableBookingWebSocketService.off('table_booking:approved', this.handleBookingApproved);
    tableBookingWebSocketService.off('table_booking:rejected', this.handleBookingRejected);
    tableBookingWebSocketService.off('table_booking:cancelled', this.handleBookingCancelled);
    tableBookingWebSocketService.off('table_booking:checked_in', this.handleCheckedIn);
    tableBookingWebSocketService.off('table_booking:completed', this.handleCompleted);
    tableBookingWebSocketService.off('table_booking:is_table_yours_updated', this.handleIsTableYoursUpdated);
    tableBookingWebSocketService.off('table_booking:is_booked_by_you_updated', this.handleIsBookedByYouUpdated);
    tableBookingWebSocketService.off('booking_message:notification', this.handleChatNotification);
    tableBookingWebSocketService.off('table:booking_opened_updated', this.handleBookingOpenedUpdated);
    tableBookingWebSocketService.off('booking_seat:requested', this.handleSeatRequested);
    tableBookingWebSocketService.off('booking_seat:assigned', this.handleSeatAssigned);
    tableBookingWebSocketService.off('table_booking:checked_out', this.handleTableCheckedOut);
    tableBookingWebSocketService.off('table:status_updated', this.handleTableStatusUpdated);
    tableBookingWebSocketService.off('booking_seat:removed', this.handleSeatRemoved);
    tableBookingWebSocketService.off('error', this.handleSocketError);
    tableBookingWebSocketService.off('connect', this.handleReconnectResync);
  };

  /**
   * Staff cleared/checked-out a table (branch-channel event). Free it in the grid immediately so the
   * customer never has to refresh. If it was this customer's own seat, also release the active-booking
   * lock so they can book again right away.
   */
  handleTableCheckedOut = (data: any) => {
    console.log('[TableSelection] Table checked out via socket:', data);
    if (!data?.tableId || (data.branchId && data.branchId !== this.state.branch?.id)) return;
    this.setState((prevState) => {
      let wasMine = false;
      const updatedAreas = prevState.areas.map((areaWithTables) => ({
        ...areaWithTables,
        tables: areaWithTables.tables.map((table) => {
          if (table.id !== data.tableId) return table;
          if ((table as any).isTableYours || (table as any).isCheckedIn) wasMine = true;
          return {
            ...table,
            status: (data.tableStatus === 'occupied' ? 'occupied' : 'available') as 'occupied' | 'available',
            isTableYours: false,
            isCheckedIn: false,
            isBookedByYou: false,
          };
        }),
      }));
      return { areas: updatedAreas, ...(wasMine ? { hasActiveBooking: false } : {}) } as any;
    });
    // Reconcile the one-active-booking lock with the server (covers the case it was ours).
    void this.checkActiveBooking();
  };

  /**
   * Staff changed a table's status directly (branch-channel event) — e.g. reserved a table for a
   * customer who phoned in without the app. Reflect the new status in the grid live so a browsing
   * customer immediately sees the table as booked/occupied and can't request it.
   */
  handleTableStatusUpdated = (data: any) => {
    console.log('[TableSelection] Table status updated via socket:', data);
    if (!data?.tableId || !data?.status) return;
    if (data.branchId && this.state.branch?.id && data.branchId !== this.state.branch.id) return;
    const nextStatus = this.mapTableStatus(String(data.status));
    this.setState((prevState) => ({
      areas: prevState.areas.map((areaWithTables) => ({
        ...areaWithTables,
        tables: areaWithTables.tables.map((table) => {
          if (table.id !== data.tableId) return table;
          // Don't stomp on the customer's own live seat/booking state.
          if ((table as any).isTableYours || (table as any).isCheckedIn || (table as any).isBookedByYou) {
            return table;
          }
          return { ...table, status: nextStatus };
        }),
      })),
    }) as any);
  };

  handleBookingApproved = (data: any) => {
    console.log('[TableSelection] Booking approved via socket:', data);
    
    // Update table status to reserved and set isTableYours to true if it matches the current branch
    // When isTableYours becomes true, ignore isBookedByYou
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? {
                  ...table,
                  status: 'reserved' as const,
                  isTableYours: true,
                  // Approved, but not yet arrived — check-in has its own event.
                  isCheckedIn: false,
                  // Ignore isBookedByYou when isTableYours is true
                  isBookedByYou: false
                }
              : table
          )
        }));
        
        return {
          areas: updatedAreas,
          socketNotification: {
            show: true,
            type: 'success',
            title: 'Booking Confirmed!',
            message: `Your booking at ${data.branchName || 'the branch'} has been confirmed. Table ${data.tableNumber || ''} is now reserved for you.`
          }
        };
      });
    } else {
      // Show notification even if not on this page
      this.setState({
        socketNotification: {
          show: true,
          type: 'success',
          title: 'Booking Confirmed!',
          message: `Your booking at ${data.branchName || 'the branch'} has been confirmed.`
        }
      });
    }

    // Update active booking if it matches
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState(prevState => ({
        activeBooking: {
          ...prevState.activeBooking,
          status: 'confirmed'
        }
      }));
    }

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      this.setState({ socketNotification: null });
    }, 5000);
  };

  handleBookingRejected = (data: any) => {
    console.log('[TableSelection] Booking rejected via socket:', data);
    
    // Update table status and clear isBookedByYou if it matches the current branch
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? { 
                  ...table, 
                  status: 'available' as const,
                  // Clear isBookedByYou when booking is rejected
                  isBookedByYou: false
                }
              : table
          )
        }));
        
        return {
          areas: updatedAreas,
          socketNotification: {
            show: true,
            type: 'error',
            title: 'Booking Rejected',
            message: data.organizerComments || data.rejectComments || `Your booking request at ${data.branchName || 'the branch'} was not approved. Please try booking another time.`
          }
        };
      });
    } else {
      this.setState({
        socketNotification: {
          show: true,
          type: 'error',
          title: 'Booking Rejected',
          message: data.organizerComments || data.rejectComments || `Your booking request at ${data.branchName || 'the branch'} was not approved. Please try booking another time.`
        }
      });
    }

    // Update active booking if it matches
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState(prevState => ({
        activeBooking: {
          ...prevState.activeBooking,
          status: 'rejected'
        }
      }));
    }

    // Auto-hide notification after 7 seconds (longer for error)
    setTimeout(() => {
      this.setState({ socketNotification: null });
    }, 7000);
  };

  handleBookingCancelled = (data: any) => {
    console.log('[TableSelection] Booking cancelled via socket:', data);
    // A cancellation may free the customer to book again — re-evaluate the rule.
    this.checkActiveBooking();

    // Update table status to available and clear flags if it matches the current branch
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? {
                  ...table,
                  status: 'available' as const,
                  isTableYours: false,
                  isCheckedIn: false,
                  isBookedByYou: false
                }
              : table
          )
        }));
        
        return {
          areas: updatedAreas,
          socketNotification: {
            show: true,
            type: 'error',
            title: 'Booking Cancelled',
            message: data.cancellationReason || `Your booking at ${data.branchName || 'the branch'} has been cancelled.`
          }
        };
      });
    } else {
      this.setState({
        socketNotification: {
          show: true,
          type: 'error',
          title: 'Booking Cancelled',
          message: data.cancellationReason || `Your booking at ${data.branchName || 'the branch'} has been cancelled.`
        }
      });
    }

    // Close chat box if it's open for this booking
    const isActiveBooking = this.state.activeBooking && this.state.activeBooking.id === data.bookingId;
    const isActiveTableChat = this.state.activeTableBookingId === data.bookingId;
    
    if (this.state.showChatBox && (isActiveBooking || isActiveTableChat)) {
      this.setState({ 
        showChatBox: false, 
        activeBooking: null,
        activeTableChat: null,
        activeTableBookingId: null
      });
    }

    // Update active booking if it matches
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState(prevState => ({
        activeBooking: {
          ...prevState.activeBooking,
          status: 'cancelled'
        }
      }));
    }

    // Auto-hide notification after 7 seconds (longer for error)
    setTimeout(() => {
      this.setState({ socketNotification: null });
    }, 7000);
  };

  handleBookingOpenedUpdated = (data: any) => {
    console.log('[TableSelection] Booking opened/closed updated:', data);
    const currentBranchId = this.props.params?.branchId;
    const isOpen = data.isBookingOpened === true;

    if (data.tableId && data.branchId === this.state.branch?.id) {
      this.setState((prev) => ({
        areas: prev.areas.map((awt) => ({
          ...awt,
          tables: awt.tables.map((t) =>
            t.id === data.tableId ? { ...t, isBookingOpened: isOpen } : t
          )
        }))
      }));
      if (currentBranchId) this.fetchChairAssignments(currentBranchId);
    }

    const tableName = data.table?.tableName || data.table?.table_name || '';
    this.setState({
      socketNotification: {
        show: true,
        type: 'info',
        title: isOpen ? 'Booking Opened' : 'Booking Closed',
        message: isOpen
          ? `Table ${tableName || data.tableId || ''} is now open for others to join.`.trim()
          : `Table ${tableName || data.tableId || ''} is no longer accepting join requests.`.trim()
      }
    });
    setTimeout(() => this.setState({ socketNotification: null }), 5000);
  };

  handleSeatRequested = (data: any) => {
    console.log('[TableSelection] Seat requested:', data);
    const branchId = this.props.params?.branchId;
    if (branchId) this.fetchChairAssignments(branchId);
  };

  handleSeatAssigned = (data: any) => {
    console.log('[TableSelection] Seat assigned:', data);
    const branchId = this.props.params?.branchId;
    if (branchId) this.fetchChairAssignments(branchId);

    this.setState({
      socketNotification: {
        show: true,
        type: 'success',
        title: 'Seat Approved!',
        message: 'Your seat request has been approved. You now have access to the table features.'
      }
    });
    setTimeout(() => this.setState({ socketNotification: null }), 5000);
  };

  handleSeatRemoved = (data: any) => {
    console.log('[TableSelection] Seat removed:', data);
    const branchId = this.props.params?.branchId;
    if (branchId) this.fetchChairAssignments(branchId);
  };

  handleSocketError = (error: any) => {
    console.error('[TableSelection] Socket error:', error);
  };

  // Realtime (re)connected. On a genuine RE-connect (not the first connect after mount), silently
  // re-sync the grid — any table/booking event published while we were offline (past the 15s Ably
  // rewind window) is otherwise lost, leaving the grid stale until a manual reload.
  private hasConnectedOnce = false;
  handleReconnectResync = () => {
    if (!this.hasConnectedOnce) { this.hasConnectedOnce = true; return; }
    const { organizerId, branchId } = this.props.params || {};
    if (organizerId && branchId) {
      console.log('[TableSelection] Realtime reconnected — silent re-sync');
      void this.loadData(organizerId, branchId, true);
      void this.checkActiveBooking();
    }
  };

  handleCheckedIn = (data: any) => {
    console.log('[TableSelection] Booking checked in via socket:', data);
    
    // Update table status to active and ensure isTableYours is true if it matches the current branch
    // When isTableYours is true, ignore isBookedByYou
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? {
                  ...table,
                  status: 'occupied' as const,
                  isTableYours: true,
                  // Checked in (arrived) — now "Call staff" is available.
                  isCheckedIn: true,
                  // Ignore isBookedByYou when isTableYours is true
                  isBookedByYou: false
                }
              : table
          )
        }));

        return {
          areas: updatedAreas,
          socketNotification: {
            show: true,
            type: 'success',
            title: 'Welcome!',
            message: data.checkinComments || `You've been checked in at ${data.branchName || 'the branch'}. Table ${data.tableNumber || ''} is ready for you.`
          }
        };
      });
    } else {
      // Show notification even if not on this page
      this.setState({
        socketNotification: {
          show: true,
          type: 'success',
          title: 'Welcome!',
          message: data.checkinComments || `You've been checked in at ${data.branchName || 'the branch'}.`
        }
      });
    }

    // Update active booking if it matches
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState(prevState => ({
        activeBooking: {
          ...prevState.activeBooking,
          status: 'active',
          checkedIn: true,
          checkedInAt: data.checkedInAt
        }
      }));
    }

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      this.setState({ socketNotification: null });
    }, 5000);
  };

  handleCompleted = (data: any) => {
    console.log('[TableSelection] Booking completed via socket:', data);

    // Checkout frees the one-active-booking lock. Clear it immediately so the customer can book
    // again right away, then reconcile with the server (in case another booking still stands).
    this.setState({ hasActiveBooking: false });
    void this.checkActiveBooking();

    // Update table status and set isTableYours to false if it matches the current branch
    // isBookedByYou may still be true (indicates customer has booked this table before)
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? {
                  ...table,
                  status: 'available' as const,
                  isTableYours: false,
                  // Checked out — no longer at the table.
                  isCheckedIn: false
                  // Keep isBookedByYou as is (it indicates historical booking)
                }
              : table
          )
        }));

        return {
          areas: updatedAreas,
          socketNotification: {
            show: true,
            type: 'info',
            title: 'Thank You!',
            message: data.checkoutComments || `Thank you for visiting ${data.branchName || 'the branch'}. We hope to see you again soon!`
          }
        };
      });
    } else {
      // Show notification even if not on this page
      this.setState({
        socketNotification: {
          show: true,
          type: 'info',
          title: 'Thank You!',
          message: data.checkoutComments || `Thank you for visiting ${data.branchName || 'the branch'}.`
        }
      });
    }

    // Update active booking if it matches
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState(prevState => ({
        activeBooking: {
          ...prevState.activeBooking,
          status: 'completed',
          paymentAmount: data.paymentAmount,
          paymentMethod: data.paymentMethod,
          paymentStatus: data.paymentStatus
        }
      }));
    }

    // Close chat if open for this booking
    if (this.state.activeBooking && this.state.activeBooking.id === data.bookingId) {
      this.setState({ 
        showChatBox: false,
        activeTableChat: null 
      });
    }

    // Auto-hide notification after 7 seconds
    setTimeout(() => {
      this.setState({ socketNotification: null });
    }, 7000);
  };

  handleIsTableYoursUpdated = (data: any) => {
    console.log('[TableSelection] isTableYours updated via socket:', data);
    
    // Update the specific table's isTableYours flag if it matches the current branch
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table =>
            table.id === data.tableId 
              ? {
                  ...table,
                  isTableYours: data.isTableYours,
                  // This event is approval/cancellation, not check-in — check-in has its own event.
                  // If ownership is cleared, drop checked-in too.
                  isCheckedIn: data.isTableYours ? (table as any).isCheckedIn : false,
                  // When ownership is cleared (checkout/cancel/reject), free the table live so it stops
                  // showing OCCUPIED without a refresh; keep it as-is when it becomes yours.
                  status: data.isTableYours ? table.status : ('available' as const),
                  // When isTableYours becomes true, ignore isBookedByYou; when cleared, drop it too.
                  isBookedByYou: false
                }
              : table
          )
        }));
        
        return { areas: updatedAreas };
      });

      // Show notification if isTableYours changed to true
      if (data.isTableYours) {
        this.setState({
          socketNotification: {
            show: true,
            type: 'success',
            title: 'Table Available',
            message: `Table ${data.booking?.tableNumber || ''} is now yours. You can chat with the organizer.`
          }
        });

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          this.setState({ socketNotification: null });
        }, 5000);
      }
    }
  };

  handleIsBookedByYouUpdated = (data: any) => {
    console.log('[TableSelection] isBookedByYou updated via socket:', data);
    
    // Only update if isTableYours is not true (isTableYours takes precedence)
    if (data.branchId === this.state.branch?.id && data.tableId) {
      this.setState(prevState => {
        const updatedAreas = prevState.areas.map(areaWithTables => ({
          ...areaWithTables,
          tables: areaWithTables.tables.map(table => {
            if (table.id === data.tableId) {
              // If isTableYours is already true, ignore isBookedByYou
              if (table.isTableYours) {
                return table; // Don't update if isTableYours is true
              }
              
              // Update isBookedByYou and set status to reserved if booking requested
              return {
                ...table,
                isBookedByYou: data.isBookedByYou,
                status: data.isBookedByYou ? 'reserved' as const : table.status
              };
            }
            return table;
          })
        }));
        
        return { areas: updatedAreas };
      });

      // Show notification if isBookedByYou changed to true
      if (data.isBookedByYou) {
        const isGrocery = this.state.branchTypeCode === 'grocery';
        this.setState({
          socketNotification: {
            show: true,
            type: 'info',
            title: isGrocery ? 'Shopping request sent' : 'Booking Request Sent',
            message: isGrocery
              ? 'Your shopping request has been sent. Waiting for organizer approval.'
              : `Your booking request for Table ${data.booking?.tableNumber || ''} has been sent. Waiting for organizer approval.`
          }
        });

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          this.setState({ socketNotification: null });
        }, 5000);
      }
    }
  };

  handleCloseNotification = () => {
    this.setState({ socketNotification: null });
  };

  loadData = async (organizerId: string, branchId: string, silent = false) => {
    // `silent` = a background re-sync (e.g. after realtime reconnects): refresh the grid in place
    // without flashing the full-screen loader, so a wifi blip doesn't blank the table list.
    if (!silent) this.setState({ isLoading: true, error: null });

    try {
      // Load organizer info from mock data (or could call API)
      const organizer = getOrganizerById(organizerId);
      
      // Call API to get branch details with areas and tables
      const apiCall = apiService.getBranchDetails(branchId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      let response: any;
      try {
        response = await Promise.race([apiCall, timeoutPromise]) as any;
      } catch (timeoutError) {
        console.warn('[TableSelection] API call timed out, using mock data');
        throw timeoutError;
      }
      
      console.log('[TableSelection] Branch details API response:', response);
      console.log('[TableSelection] Full response structure:', JSON.stringify(response, null, 2));

      if (response) {
        const payload: any = response as any;
        const data: any = (payload && (payload.data ?? payload)) ?? {};
        const branchData = data.branch || data;
        
        // Try multiple possible structures for areas
        let areasData = data.areas || [];
        
        // If areas is not directly in data, check if it's nested differently
        if (!areasData || areasData.length === 0) {
          // Check if areas are in branchData
          areasData = branchData.areas || [];
        }
        
        // If still no areas, check if tables are directly in data
        if (!areasData || areasData.length === 0) {
          const directTables = data.tables || branchData.tables || [];
          if (directTables.length > 0) {
            // Create a default area with all tables
            areasData = [{
              area: {
                id: 'default-area',
                name: 'Main Area',
                isActive: true
              },
              tables: directTables
            }];
          }
        }
        
        console.log('[TableSelection] Extracted areasData:', areasData);
        console.log('[TableSelection] Areas count:', areasData?.length || 0);

        // Map branch data
        const addressParts = [
          branchData.address,
          branchData.city,
          branchData.state,
          branchData.country
        ].filter(Boolean);
        const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : branchData.address || 'Address not available';

        const mappedBranch: Branch = {
          id: branchId,
          organizerId: organizerId,
          name: branchData.name || 'Branch',
          address: fullAddress,
          phone: branchData.phone || '+1 (555) 123-4567',
          email: branchData.email || 'contact@branch.com',
          status: (branchData.isActive === true || branchData.status === 'active') ? 'active' : 'inactive',
          tableCount: 0, // Will be calculated from tables
          availableTables: 0, // Will be calculated from tables
          images: branchData.images || undefined
        };

        // Map areas and tables
        const mappedAreas: AreaWithTables[] = (areasData || []).map((areaData: any) => {
          // Handle different area structures
          const areaObj = areaData.area || areaData;
          const area: Area = {
            id: areaObj.id || areaData.areaId || `area-${Date.now()}-${Math.random()}`,
            branchId: branchId,
            name: areaObj.name || areaData.name || 'Unnamed Area',
            description: areaObj.description || areaData.description,
            isActive: areaObj.isActive !== false && areaObj.isActive !== undefined 
              ? areaObj.isActive 
              : (areaData.isActive !== false && areaData.isActive !== undefined ? areaData.isActive : true)
          };

          // Map tables for this area - try multiple possible structures
          let tablesArray = areaData.tables || [];
          
          // If tables are directly in areaData (not nested in areaData.tables)
          if (!tablesArray || tablesArray.length === 0) {
            // Check if tables are at the same level as area
            tablesArray = areaData.table || [];
          }
          
          // If still no tables, check if areaData itself is a table (flat structure)
          if (!tablesArray || tablesArray.length === 0) {
            // Check if this might be a flat table structure
            // Check both table_number (snake_case from API) and tableNumber (camelCase)
            if (areaData.id && (
              areaData.table_number !== undefined || 
              areaData.tableNumber !== undefined || 
              areaData.capacity !== undefined
            )) {
              tablesArray = [areaData];
            }
          }
          
          console.log(`[TableSelection] Area "${area.name}" has ${tablesArray?.length || 0} tables`);
          
          const tables: Table[] = (tablesArray || []).map((tableData: any) => {
            // Handle table_number - can be integer (new format) or string (old format)
            let tableNumber = 0;
            let originalTableNumber: string | number = '';
            
            // Check if table_number exists (snake_case from API) or tableNumber (camelCase)
            const tableNumberValue = tableData.table_number !== undefined ? tableData.table_number : tableData.tableNumber;
            
            if (tableNumberValue !== undefined && tableNumberValue !== null) {
              if (typeof tableNumberValue === 'number') {
                // New format: integer directly from API
                tableNumber = tableNumberValue;
                originalTableNumber = tableNumberValue;
              } else if (typeof tableNumberValue === 'string') {
                // Old format: string like "T-01" or "VIP-01"
                const tableNumberMatch = tableNumberValue.match(/\d+/);
                tableNumber = tableNumberMatch ? parseInt(tableNumberMatch[0]) : 0;
                originalTableNumber = tableNumberValue;
              }
            }
            
            // Fallback if no table number found
            if (tableNumber === 0 && !originalTableNumber) {
              originalTableNumber = `T-${tableNumber}`;
            }
            
            // Debug log for table number parsing
            if (tableNumber === 0) {
              console.warn('[TableSelection] Table number is 0 for table:', {
                tableId: tableData.id,
                table_number: tableData.table_number,
                tableNumber: tableData.tableNumber,
                tableNumberValue,
                type: typeof tableNumberValue
              });
            }

            // Determine table status based on isTableYours and isBookedByYou
            // Priority: isTableYours > isBookedByYou
            let tableStatus = this.mapTableStatus(tableData.status);
            const isTableYours = tableData.isTableYours === true;
            const isBookedByYou = tableData.isBookedByYou === true;
            
            // If isTableYours is true, ignore isBookedByYou
            // If isTableYours is false but isBookedByYou is true, show as reserved (booking requested)
            if (isTableYours) {
              // Table is yours (approved/active), ignore isBookedByYou
              // Status should already be set correctly from API (reserved/occupied)
            } else if (isBookedByYou) {
              // Booking request pending, show as reserved
              tableStatus = 'reserved';
            }

            const tableName = (tableData.tableName ?? tableData.table_name ?? '').trim() || undefined;
            const isBookingOpened = tableData.isBookingOpened === true || tableData.is_booking_opened === true;
            return {
              id: tableData.id || `table-${Date.now()}-${Math.random()}`,
              branchId: branchId,
              number: tableNumber || 0,
              capacity: Number(tableData.capacity) || 4,
              status: tableStatus,
              location: tableData.location || area.name,
              category: area.name,
              images: tableData.images || undefined,
              imageUrl: tableData.imageUrl || tableData.image_url || undefined,
              features: tableData.notes ? [tableData.notes] : undefined,
              isTableYours: isTableYours,
              isCheckedIn: tableData.isCheckedIn === true,
              isBookedByYou: isBookedByYou && !isTableYours,
              isBookingOpened: isBookingOpened,
              tableNumber: originalTableNumber,
              tableName: tableName || undefined
            } as Table & { tableNumber?: string; tableName?: string; imageUrl?: string };
          });

          return { area, tables };
        });

        // Calculate total tables and available tables
        const allTables = mappedAreas.flatMap(awt => awt.tables);
        mappedBranch.tableCount = allTables.length;
        mappedBranch.availableTables = allTables.filter(t => t.status === 'available').length;

        console.log('[TableSelection] Mapped branch and areas:', { branch: mappedBranch, areas: mappedAreas });
        console.log('[TableSelection] Total tables found:', allTables.length);
        console.log('[TableSelection] Areas count:', mappedAreas.length);
        mappedAreas.forEach((awt, idx) => {
          console.log(`[TableSelection] Area ${idx + 1} (${awt.area.name}): ${awt.tables.length} tables`);
        });

        // If no tables found, log warning
        if (allTables.length === 0) {
          console.warn('[TableSelection] No tables found in API response. Response structure:', {
            response,
            data,
            branchData,
            areasData
          });
        }

        // Branch type from API (e.g. { id, code: "grocery", label: "Grocery Store" })
        const branchType = branchData.branchType ?? data.branchType;
        const branchTypeCode = (branchType && typeof branchType === 'object' && branchType.code) ? branchType.code : null;
        const branchCurrency = (branchData.currency ?? data.currency) || null;
        const stockItemsByCategory = Array.isArray(data.stockItemsByCategory) ? data.stockItemsByCategory : [];

        this.setState({
          branch: mappedBranch,
          areas: mappedAreas,
          organizer: organizer || null,
          isLoading: false,
          branchTypeCode: branchTypeCode ?? null,
          branchCurrency,
          stockItemsByCategory
        });

        this.fetchChairAssignments(branchId);
        this.fetchBranchFeatures(branchId);

        // For grocery branch, load my shopping request and setup socket for live status
        if (branchTypeCode === 'grocery') {
          this.loadMyShoppingRequest(branchId, mappedBranch?.name)
            .catch(() => this.setState({ myShoppingRequest: null, shoppingRequestUnreadCount: 0 }));
        } else {
          this.setState({ myShoppingRequest: null, shoppingRequestUnreadCount: 0 });
          shoppingRequestWebSocketService.disconnect();
        }
      } else {
        // Fallback to mock data
        this.loadMockData(organizerId, branchId);
      }
    } catch (error: any) {
      console.error('[TableSelection] Failed to load branch details:', error);
      // Fallback to mock data on error
      this.loadMockData(organizerId, branchId);
      this.setState({
        error: error?.message?.includes('timeout')
          ? 'Request timed out. Showing sample data.'
          : 'Failed to load branch details. Showing sample data.'
      });
    }
  };

  /** Load the branch's resolved feature pillars (branch-type aware) so we can hide bar-only extras
   *  (e.g. Live Song Request) on venue types that don't have them — a restaurant won't show songs. */
  fetchBranchFeatures = async (branchId: string) => {
    try {
      const res: any = await apiService.getBranchFeatures(branchId);
      const list = Array.isArray(res?.features) ? res.features : (Array.isArray(res?.data?.features) ? res.data.features : null);
      // On an unreadable response, fail CLOSED (empty) so bar-only extras stay hidden rather than leak.
      this.setState({ branchFeatures: Array.isArray(list) ? list : [] });
    } catch {
      this.setState({ branchFeatures: [] });
    }
  };

  /** True when the branch has a given feature pillar. Hidden until the feature list loads. */
  branchHasFeature = (key: string): boolean => Array.isArray(this.state.branchFeatures) && this.state.branchFeatures.includes(key);

  loadMockData = (organizerId: string, branchId: string) => {
    const branch = getBranchById(branchId);
    const tables = getTablesByBranchId(branchId);
    const organizer = getOrganizerById(organizerId);

    // Group tables by location/category for mock data
    const tablesByLocation = new Map<string, Table[]>();
    tables.forEach(table => {
      const location = table.location || table.category || 'General Area';
      if (!tablesByLocation.has(location)) {
        tablesByLocation.set(location, []);
      }
      tablesByLocation.get(location)!.push(table);
    });

    // Convert to AreaWithTables format
    const areas: AreaWithTables[] = Array.from(tablesByLocation.entries()).map(([location, locationTables]) => ({
      area: {
        id: `area-${location.toLowerCase().replace(/\s+/g, '-')}`,
        branchId: branchId,
        name: location,
        description: `Tables in ${location}`,
        isActive: true
      },
      tables: locationTables
    }));

    this.setState({
      branch: branch || null,
      areas: areas.length > 0 ? areas : [],
      organizer: organizer || null,
      isLoading: false,
      branchTypeCode: null,
      branchCurrency: null
    });
  };

  fetchChairAssignments = async (branchId: string) => {
    try {
      const [seatsRes, openRes] = await Promise.all([
        apiService.getMyBookingSeats(),
        apiService.getOpenForJoinBookings(branchId).catch(() => null)
      ]);

      // Process my seats
      const rawSeats = (seatsRes as any)?.data ?? seatsRes;
      const seats: any[] = Array.isArray(rawSeats?.seats) ? rawSeats.seats : (Array.isArray(rawSeats) ? rawSeats : []);
      const assignments: Record<string, string> = {};
      const pendingRequests: Record<string, string> = {};

      for (const seat of seats) {
        const booking = seat.booking;
        if (!booking) continue;
        if (booking.branchId !== branchId) continue;
        const tableId = booking.tableId || booking.table?.id;
        const bookingId = seat.bookingId || booking.id;
        const seatStatus = (seat.status || '').toLowerCase();

        if (tableId && bookingId) {
          if (seatStatus === 'approved') {
            assignments[tableId] = bookingId;
          } else if (seatStatus === 'pending') {
            pendingRequests[tableId] = seat.id;
          }
        }
      }

      // Process open-for-join bookings (store full objects for join mode)
      const openForJoinMap: Record<string, string> = {};
      const openForJoinBookingDetails: Record<string, any> = {};
      if (openRes) {
        const raw = openRes as any;
        const data = raw?.data ?? raw;
        const bookings: any[] = Array.isArray(data?.bookings) ? data.bookings : (Array.isArray(data) ? data : Array.isArray(raw?.bookings) ? raw.bookings : []);
        for (const b of bookings) {
          const tableId = String(b.tableId || b.table?.id || b.table_id || '').trim();
          const bid = b.id || b.bookingId || b.booking_id;
          if (tableId && bid) {
            openForJoinMap[tableId] = String(bid);
            openForJoinBookingDetails[tableId] = b;
          }
        }
      }

      this.setState((prev) => {
        const newAreas = prev.areas.map((awt) => ({
          ...awt,
          tables: awt.tables.map((t) => {
            const assignedBookingId = assignments[t.id];
            const hasPending = !!pendingRequests[t.id];
            const openBookingId = openForJoinMap[t.id];

            if (assignedBookingId) {
              return { ...t, isAssignedToYou: true, assignedBookingId, seatRequestStatus: 'approved' as const, openForJoinBookingId: openBookingId } as Table & { tableNumber?: string; tableName?: string };
            }
            if (hasPending) {
              return { ...t, isAssignedToYou: false, assignedBookingId: undefined, seatRequestStatus: 'pending' as const, openForJoinBookingId: openBookingId } as Table & { tableNumber?: string; tableName?: string };
            }
            return { ...t, isAssignedToYou: (prev.chairAssignments[t.id] ? false : t.isAssignedToYou), seatRequestStatus: null, openForJoinBookingId: openBookingId } as Table & { tableNumber?: string; tableName?: string };
          })
        }));
        return { chairAssignments: assignments, pendingSeatRequests: pendingRequests, openForJoinMap, openForJoinBookingDetails, areas: newAreas };
      });
    } catch {
      // Chair assignments not available
    }
  };

  mapTableStatus = (status: string): 'available' | 'occupied' | 'reserved' | 'maintenance' => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'available' || statusLower === 'free') return 'available';
    if (statusLower === 'occupied' || statusLower === 'busy') return 'occupied';
    if (statusLower === 'reserved' || statusLower === 'booked') return 'reserved';
    if (statusLower === 'maintenance' || statusLower === 'maintenance') return 'maintenance';
    return 'available'; // Default
  };

  handleTableClick = (table: Table) => {
    const isJoin = !!(table.isBookingOpened && !table.isTableYours && !table.isAssignedToYou && table.seatRequestStatus !== 'pending');
    // One active booking at a time: don't open the booking/join flow if the customer
    // already holds an active booking (grocery branches are exempt).
    if (this.state.hasActiveBooking && this.state.branchTypeCode !== 'grocery') {
      this.setState({
        socketNotification: {
          show: true,
          type: 'info',
          title: 'You already have an active booking',
          message: 'Please complete or cancel your current booking before booking another table.',
        },
      });
      setTimeout(() => this.setState({ socketNotification: null }), 6000);
      return;
    }
    if (isJoin || (table.status === 'available' && !table.isBookedByYou)) {
      const joinBooking = isJoin ? (this.state.openForJoinBookingDetails[table.id] || null) : null;
      // Prefill the form with today + a valid near-future time (LOCAL, not UTC — avoids the
      // "time already passed" / timezone mismatch) so the customer can submit right away or adjust.
      const now = new Date();
      const soon = new Date(now.getTime() + 10 * 60000); // +10 min buffer
      // If +10min would spill past midnight, pull it back so it stays within today.
      if (soon.getDate() !== now.getDate()) { soon.setTime(now.getTime()); soon.setHours(23, 55, 0, 0); }
      const pad = (n: number) => String(n).padStart(2, '0');
      const localDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const localTime = `${pad(soon.getHours())}:${pad(soon.getMinutes())}`;
      this.setState((prev) => ({
        selectedTable: table,
        showBookingModal: true,
        isJoinMode: isJoin,
        joinMainBooking: joinBooking,
        chairsStatus: [],
        selectedChairNumber: null,
        isLoadingChairs: true,
        bookingError: null,
        bookingSuccess: false,
        bookingForm: {
          ...prev.bookingForm,
          bookingDate: prev.bookingForm.bookingDate || localDate,
          startTime: prev.bookingForm.startTime || localTime,
          durationMinutes: prev.bookingForm.durationMinutes || 60,
          numberOfCustomers: prev.bookingForm.numberOfCustomers || (table as any).capacity || 2,
        },
      }), () => {
        if (isJoin) this.fetchChairsStatusForJoin(table, joinBooking);
      });
    }
  };

  /** Open the booking-request form for this table. The customer fills in the details (guests, time,
   *  notes) and submits → creates a 'requested' booking that staff review & approve; check-in happens
   *  when the customer physically arrives. No instant self-check-in. Reuses the existing booking modal. */
  handleRequestTable = (table: Table) => {
    this.handleTableClick(table);
  };

  /** Instant walk-in: grab an AVAILABLE table and check in immediately (no organizer approval).
   *  Additional to the date/time reservation flow — used when the customer is physically at the venue. */
  handleSitNow = async (table: Table) => {
    // Guard against double-taps while a request is in flight.
    if (this.state.sittingNowTableId) return;
    // Respect the one-active-booking rule client-side (grocery branches are exempt).
    if (this.state.hasActiveBooking && this.state.branchTypeCode !== 'grocery') {
      this.setState({
        socketNotification: {
          show: true,
          type: 'info',
          title: 'You already have an active booking',
          message: 'Please complete or cancel your current booking before sitting at another table.',
        },
      });
      setTimeout(() => this.setState({ socketNotification: null }), 6000);
      return;
    }
    this.setState({ sittingNowTableId: table.id });
    try {
      const res = await apiService.sitNow(table.id);
      if (res.success) {
        // Re-run the existing load/booking-resolve logic so the checked-in (isTableYours +
        // isCheckedIn) derived state, active-booking flag and realtime affordances stay consistent.
        const { organizerId, branchId } = this.props.params || {};
        if (organizerId && branchId) await this.loadData(organizerId, branchId);
        await this.checkActiveBooking();
        const tableName = (table as any).tableName || `Table ${table.number}`;
        this.setState({
          socketNotification: {
            show: true,
            type: 'success',
            title: "You're checked in",
            message: `You're now seated at ${tableName}. Use Call staff, Order or View bill below.`,
          },
        });
        setTimeout(() => this.setState({ socketNotification: null }), 6000);
      } else {
        throw new Error((res as any)?.message || 'Could not sit at this table.');
      }
    } catch (err: any) {
      // Surface the server message (e.g. 409 already-active-booking, 400 table-not-available).
      const message = err?.message || 'Could not sit at this table. Please try again.';
      this.setState({
        socketNotification: { show: true, type: 'error', title: 'Unable to sit here', message },
      });
      setTimeout(() => this.setState({ socketNotification: null }), 6000);
      // Refresh so the one-active-booking flag reflects reality after a 409.
      this.checkActiveBooking();
    } finally {
      this.setState({ sittingNowTableId: null });
    }
  };

  /** "I'm here — seat me" fast path: one tap sends a booking request for NOW (no date/time form).
   *  It lands as 'requested', so staff still approve + check the customer in — this is NOT a
   *  self-check-in (the locked booking rule). Removes only the time-picking friction for a guest
   *  who is already standing in the venue. Distinct from handleSitNow (instant, no approval). */
  handleImHere = async (table: Table) => {
    if (this.state.imHereTableId) return;
    // Respect the one-active-booking rule client-side (grocery branches are exempt).
    if (this.state.hasActiveBooking && this.state.branchTypeCode !== 'grocery') {
      this.setState({
        socketNotification: {
          show: true,
          type: 'info',
          title: 'You already have an active booking',
          message: 'Please complete or cancel your current booking before requesting another table.',
        },
      });
      setTimeout(() => this.setState({ socketNotification: null }), 6000);
      return;
    }
    this.setState({ imHereTableId: table.id });
    try {
      const res = await apiService.imHereNow(table.id);
      if (res.success) {
        // Refresh so the table now reflects the pending request (isBookedByYou) and the
        // one-active-booking flag stays consistent — same as the normal request flow.
        const { organizerId, branchId } = this.props.params || {};
        if (organizerId && branchId) await this.loadData(organizerId, branchId);
        await this.checkActiveBooking();
        const tableName = (table as any).tableName || `Table ${table.number}`;
        this.setState({
          socketNotification: {
            show: true,
            type: 'success',
            title: 'Sent to staff',
            message: `We’ve let staff know you’re at ${tableName}. They’ll confirm and seat you shortly.`,
          },
        });
        setTimeout(() => this.setState({ socketNotification: null }), 6000);
      } else {
        throw new Error((res as any)?.message || 'Could not send your request.');
      }
    } catch (err: any) {
      const message = err?.message || 'Could not send your request. Please try again.';
      this.setState({
        socketNotification: { show: true, type: 'error', title: 'Unable to request this table', message },
      });
      setTimeout(() => this.setState({ socketNotification: null }), 6000);
      // Refresh so the one-active-booking flag reflects reality after a 409.
      this.checkActiveBooking();
    } finally {
      this.setState({ imHereTableId: null });
    }
  };

  handleCloseModal = () => {
    this.setState({
      showBookingModal: false, 
      selectedTable: null,
      bookingError: null,
      bookingSuccess: false,
      chairsStatus: [],
      isLoadingChairs: false,
      selectedChairNumber: null,
      isJoinMode: false,
      joinMainBooking: null
    });
  };

  fetchChairsStatus = async () => {
    const { selectedTable, branch, bookingForm } = this.state;
    if (!selectedTable || !branch) return;
    if (!bookingForm.bookingDate || !bookingForm.startTime || !bookingForm.durationMinutes) return;

    const startParts = bookingForm.startTime.split(':');
    if (startParts.length < 2) return;
    const startH = parseInt(startParts[0]) || 0;
    const startM = parseInt(startParts[1]) || 0;
    const totalMinutes = startH * 60 + startM + (bookingForm.durationMinutes || 60);
    const endH = Math.min(Math.floor(totalMinutes / 60), 23);
    const endM = totalMinutes % 60;
    const endTime = `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;

    this.setState({ isLoadingChairs: true, chairsStatus: [], selectedChairNumber: null });
    try {
      const res = await apiService.getChairsStatus(selectedTable.id, {
        branchId: branch.id,
        bookingDate: bookingForm.bookingDate,
        startTime: bookingForm.startTime,
        endTime
      });
      const raw = (res as any)?.data ?? res;
      const chairs = Array.isArray(raw?.chairs) ? raw.chairs : (Array.isArray(raw) ? raw : []);
      const chair1 = chairs.find((c: any) => (c.chairNumber ?? c.chair_number) === 1);
      const autoChair1 = chair1?.status === 'available' ? 1 : null;
      this.setState({ chairsStatus: chairs, isLoadingChairs: false, selectedChairNumber: autoChair1 ?? null });
    } catch {
      this.setState({ chairsStatus: [], isLoadingChairs: false });
    }
  };

  fetchChairsStatusForJoin = async (table?: Table, mainBooking?: any) => {
    const t = table || this.state.selectedTable;
    let booking = mainBooking || this.state.joinMainBooking;
    const { branch } = this.state;
    if (!t || !branch) {
      this.setState({ isLoadingChairs: false });
      return;
    }
    const branchId = this.props.params?.branchId || branch.id;
    if (!booking || !(booking.bookingDate || booking.booking_date)) {
      try {
        const openRes = await apiService.getOpenForJoinBookings(branchId);
        const raw = (openRes as any);
        const data = raw?.data ?? raw;
        const bookings: any[] = Array.isArray(data?.bookings) ? data.bookings : (Array.isArray(data) ? data : Array.isArray(raw?.bookings) ? raw.bookings : []);
        const tableIdStr = String(t.id);
        const match = bookings.find((b: any) => String(b.tableId || b.table?.id || b.table_id || '').trim() === tableIdStr);
        if (match) {
          booking = match;
          this.setState({ joinMainBooking: match });
        }
      } catch { /* continue with fallback */ }
    }
    let bookingDate = booking?.bookingDate || booking?.booking_date;
    let startTime = (booking?.startTime || booking?.start_time || '').substring(0, 5);
    let endTime = (booking?.endTime || booking?.end_time || '').substring(0, 5);
    if (!bookingDate || !startTime || !endTime) {
      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      bookingDate = bookingDate || today;
      startTime = startTime || `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      endTime = endTime || `${Math.min(h + 2, 23).toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    try {
      const res = await apiService.getChairsStatus(t.id, {
        branchId: branch.id,
        bookingDate,
        startTime,
        endTime
      });
      const raw = (res as any)?.data ?? res;
      const chairs = Array.isArray(raw?.chairs) ? raw.chairs : (Array.isArray(raw) ? raw : []);
      this.setState({ chairsStatus: chairs, isLoadingChairs: false });
    } catch {
      this.setState({ chairsStatus: [], isLoadingChairs: false });
    }
  };

  handleCloseRequestSeatModal = () => {
    this.setState({ showRequestSeatModal: false, requestSeatTable: null, requestSeatError: null });
  };

  handleRequestSeat = async () => {
    const { requestSeatTable } = this.state;
    if (!requestSeatTable) return;

    this.setState({ isRequestingSeat: true, requestSeatError: null });
    try {
      // Try cached bookingId first, then from state map, then fetch fresh from API
      let bookingId = requestSeatTable.openForJoinBookingId || this.state.openForJoinMap[requestSeatTable.id];

      if (!bookingId) {
        const branchId = this.props.params?.branchId;
        if (!branchId) throw new Error('Branch not found');
        const openRes = await apiService.getOpenForJoinBookings(branchId);
        const rawOpen = (openRes as any)?.data ?? openRes;
        const bookings: any[] = Array.isArray(rawOpen?.bookings) ? rawOpen.bookings : (Array.isArray(rawOpen) ? rawOpen : []);
        const match = bookings.find((b: any) => (b.tableId || b.table?.id) === requestSeatTable.id);
        bookingId = match?.id;
      }

      if (!bookingId) {
        throw new Error('No open booking found for this table. Please try again.');
      }

      const res = await apiService.requestSeat(bookingId) as any;
      if (res && res.success === false) {
        throw new Error(res.message || 'Failed to request seat');
      }
      this.setState({
        isRequestingSeat: false,
        showRequestSeatModal: false,
        requestSeatTable: null,
        socketNotification: {
          show: true,
          type: 'info',
          title: 'Seat Requested',
          message: 'Your request to join this table has been sent. Waiting for organizer approval.'
        }
      });
      setTimeout(() => this.setState({ socketNotification: null }), 5000);
      const branchId = this.props.params?.branchId;
      if (branchId) this.fetchChairAssignments(branchId);
    } catch (err: any) {
      this.setState({ isRequestingSeat: false, requestSeatError: err?.message || 'Failed to request seat. Please try again.' });
    }
  };

  /**
   * Fetch my shopping request for a branch and put it into state (authoritative).
   * Prefers a still-open request (`requested`/`confirmed`/`active`) so a pending
   * request always gates the "Send request" button; falls back to the most recent
   * (e.g. terminal) match only when there is no open request. Also wires up the
   * live-status socket when an open request exists.
   */
  loadMyShoppingRequest = async (branchId: string, branchName?: string) => {
    const res: any = await apiService.getShoppingRequests();
    const raw = res?.data ?? res;
    const list = raw ?? [];
    const arr = Array.isArray(list)
      ? list
      : (list && typeof list === 'object'
        ? (list.shoppingRequests ?? list.requests ?? list.content ?? list.data ?? list.items ?? [])
        : []);
    const safeArr = Array.isArray(arr) ? arr : [];
    const matches = safeArr.filter((r: any) => (r.branchId || r.branch?.id) === branchId);
    const open = matches.find((r: any) => TableSelection.OPEN_SHOPPING_STATUSES.includes(String(r.status)));
    const forBranch = open ?? matches[0] ?? null;
    const req = forBranch
      ? { id: String(forBranch.id), status: String(forBranch.status || 'requested'), branchId: String(forBranch.branchId || branchId), branch: forBranch.branch || { name: branchName } }
      : null;
    this.setState({ myShoppingRequest: req });
    if (req && TableSelection.OPEN_SHOPPING_STATUSES.includes(req.status)) {
      this.setupShoppingRequestSocket(req.id);
    }
    return req;
  };

  /** Send grocery/shopping request: POST /customers/shopping-requests (branchId + optional notes). */
  handleGrocerySendRequest = async () => {
    const { branch } = this.state;
    if (!branch) return;

    // Only one open request per store: if one is already pending/accepted, do not
    // create another — the customer must wait for the organizer to respond. This
    // guards against double-clicks and any stale render where the button is shown.
    const existing = this.state.myShoppingRequest;
    if (existing && TableSelection.OPEN_SHOPPING_STATUSES.includes(existing.status)) {
      this.setState({
        socketNotification: {
          show: true,
          type: 'info',
          title: 'Request already sent',
          message: 'You already have a shopping request for this store. Please wait for the organizer to respond.'
        }
      });
      setTimeout(() => this.setState({ socketNotification: null }), 5000);
      return;
    }

    this.setState({ isSubmittingBooking: true, bookingError: null });
    try {
      const response = await apiService.createShoppingRequest({ branchId: branch.id }) as any;
      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to send shopping request');
      }
      // Re-derive state from the server so the gate is authoritative even if the
      // POST response shape varies (the request was created server-side regardless).
      const req = await this.loadMyShoppingRequest(branch.id, branch.name);
      this.setState({
        isSubmittingBooking: false,
        socketNotification: req
          ? {
            show: true,
            type: 'info',
            title: 'Shopping request sent',
            message: 'Your shopping request has been sent. Waiting for organizer approval.'
          }
          : null
      });
      if (!req) {
        throw new Error('Request sent, but could not be confirmed. Please refresh.');
      }
      setTimeout(() => this.setState({ socketNotification: null }), 5000);
    } catch (err: any) {
      this.setState({ isSubmittingBooking: false, bookingError: err?.message || 'Failed to send shopping request. Please try again.' });
    }
  };

  /** Connect socket and subscribe to shopping request status events for live updates. */
  setupShoppingRequestSocket = (requestId: string) => {
    shoppingRequestWebSocketService.disconnect();
    shoppingRequestWebSocketService.connect(requestId);
    const pid = (p: any) => p?.requestId ?? p?.data?.requestId ?? p?.id;
    shoppingRequestWebSocketService.on('shopping_request:approved', (payload: any) => {
      if (pid(payload) !== requestId) return;
      const status = payload?.status ?? payload?.data?.status ?? 'confirmed';
      this.setState(prev => prev.myShoppingRequest?.id === requestId ? { ...prev, myShoppingRequest: { ...prev.myShoppingRequest!, status: String(status) }, socketNotification: { show: true, type: 'success', title: 'Request approved', message: 'Your shopping request was approved. You can chat with the store.' } } : prev);
      setTimeout(() => this.setState(s => ({ ...s, socketNotification: null })), 5000);
      apiService.getShoppingRequest(requestId).then((res: any) => {
        const raw = res?.data ?? res;
        if (raw?.id && (raw.branchId === this.state.branch?.id || raw.branch?.id === this.state.branch?.id)) {
          this.setState(prev => prev.myShoppingRequest?.id === raw.id ? { ...prev, myShoppingRequest: { id: String(raw.id), status: String(raw.status || 'confirmed'), branchId: String(raw.branchId || this.state.branch?.id), branch: raw.branch || prev.myShoppingRequest?.branch } } : prev);
        }
      }).catch(() => {});
    });
    shoppingRequestWebSocketService.on('shopping_request:rejected', (payload: any) => {
      if (pid(payload) !== requestId) return;
      this.setState(prev => prev.myShoppingRequest?.id === requestId ? { ...prev, myShoppingRequest: { ...prev.myShoppingRequest!, status: 'rejected' }, socketNotification: { show: true, type: 'error', title: 'Request rejected', message: payload?.rejectComments ?? payload?.data?.rejectComments ?? 'Your request was rejected.' } } : prev);
      setTimeout(() => this.setState(s => ({ ...s, socketNotification: null })), 5000);
    });
    shoppingRequestWebSocketService.on('shopping_request:checked_in', (payload: any) => {
      if (pid(payload) !== requestId) return;
      this.setState(prev => prev.myShoppingRequest?.id === requestId ? { ...prev, myShoppingRequest: { ...prev.myShoppingRequest!, status: 'active' }, socketNotification: { show: true, type: 'info', title: 'Checked in', message: 'You are checked in. Happy shopping!' } } : prev);
      setTimeout(() => this.setState(s => ({ ...s, socketNotification: null })), 5000);
    });
    shoppingRequestWebSocketService.on('shopping_request:completed', (payload: any) => {
      if (pid(payload) !== requestId) return;
      this.setState(prev => prev.myShoppingRequest?.id === requestId ? { ...prev, myShoppingRequest: { ...prev.myShoppingRequest!, status: 'completed' }, socketNotification: { show: true, type: 'success', title: 'Completed', message: 'Checkout completed. Thank you!' } } : prev);
      setTimeout(() => this.setState(s => ({ ...s, socketNotification: null })), 5000);
    });
    shoppingRequestWebSocketService.on('shopping_request:cancelled', (payload: any) => {
      if (pid(payload) !== requestId) return;
      this.setState(prev => prev.myShoppingRequest?.id === requestId ? { ...prev, myShoppingRequest: { ...prev.myShoppingRequest!, status: 'cancelled' }, socketNotification: { show: true, type: 'info', title: 'Cancelled', message: 'The request was cancelled.' } } : prev);
      setTimeout(() => this.setState(s => ({ ...s, socketNotification: null })), 5000);
    });
    const handleNewMessage = (payload: any) => {
      const msgRequestId = payload?.requestId ?? payload?.data?.requestId ?? payload?.request?.id;
      if (msgRequestId && msgRequestId !== requestId) return;
      const senderType = payload?.senderType ?? payload?.data?.senderType ?? payload?.sender?.type;
      if (senderType === 'customer') return;
      // The `shopping_request_message:notification` event carries the authoritative
      // unread count for this request (accurate in both directions per backend).
      // Prefer it; fall back to incrementing for the bare `:new` event.
      const unreadCount = payload?.unreadCount ?? payload?.data?.unreadCount;
      this.setState(prev => {
        if (prev.showShoppingRequestChat) return prev;
        return {
          ...prev,
          shoppingRequestUnreadCount:
            typeof unreadCount === 'number'
              ? unreadCount
              : (prev.shoppingRequestUnreadCount ?? 0) + 1
        };
      });
    };
    shoppingRequestWebSocketService.on('shopping_request_message:new', handleNewMessage);
    shoppingRequestWebSocketService.on('shopping_request_message:notification', handleNewMessage);
  };

  /** Open shopping request chat (grocery). */
  handleOpenShoppingRequestChat = () => {
    this.setState({ showShoppingRequestChat: true, shoppingRequestUnreadCount: 0 });
  };

  handleCloseShoppingRequestChat = () => {
    // The chat component disconnects the shared socket on unmount, so re-establish
    // the user-channel subscription after closing to keep the unread badge live
    // while the chat box is closed.
    const requestId = this.state.myShoppingRequest?.id;
    this.setState({ showShoppingRequestChat: false }, () => {
      if (requestId) this.setupShoppingRequestSocket(requestId);
    });
  };

  /** Shop: search filter change */
  handleShopSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ shopSearchQuery: (e.target.value || '').trim().toLowerCase() });
  };

  /** Shop: select category → show subcategories */
  handleShopSelectCategory = (categoryId: string) => {
    this.setState({ shopSelectedCategoryId: categoryId, shopSelectedSubCategoryId: null });
  };

  /** Shop: select subcategory → show items */
  handleShopSelectSubCategory = (subCategoryId: string) => {
    this.setState({ shopSelectedSubCategoryId: subCategoryId });
  };

  /** Shop: back to categories (clear category and sub) */
  handleShopBackToCategories = () => {
    this.setState({ shopSelectedCategoryId: null, shopSelectedSubCategoryId: null });
  };

  /** Shop: back to subcategories (clear sub only) */
  handleShopBackToSubCategories = () => {
    this.setState({ shopSelectedSubCategoryId: null });
  };

  /** Shop: show more items for a section */
  handleShopShowMore = (key: string) => {
    this.setState(prev => ({
      shopVisibleCount: { ...prev.shopVisibleCount, [key]: (prev.shopVisibleCount[key] || 24) + 24 }
    }));
  };

  /** Shop: add item to cart (quantity 1 or delta). */
  handleShopAddToCart = (stockItemId: string, addQuantity: number = 1) => {
    this.setState(prev => {
      const q = (prev.shopCart[stockItemId] || 0) + addQuantity;
      const next = { ...prev.shopCart };
      if (q <= 0) delete next[stockItemId]; else next[stockItemId] = q;
      return { shopCart: next, shopCartError: null };
    });
  };

  /** Shop: remove one or all of item from cart. */
  handleShopRemoveFromCart = (stockItemId: string, removeAll?: boolean) => {
    this.setState(prev => {
      const next = { ...prev.shopCart };
      if (removeAll) delete next[stockItemId];
      else {
        const q = (next[stockItemId] || 1) - 1;
        if (q <= 0) delete next[stockItemId]; else next[stockItemId] = q;
      }
      return { shopCart: next, shopCartError: null };
    });
  };

  /** Shop: clear entire cart. */
  handleShopClearCart = () => {
    this.setState({ shopCart: {}, shopCartError: null });
  };

  /**
   * Build item info map (name + unit) from stockItemsByCategory for message formatting.
   * Unit is normalized from itemPriceWithDetails / itemUnitDescription / itemUnit (e.g. "per dozen" → "dozen").
   */
  getShopCartItemInfoMap = (): Record<string, { itemName: string; unit: string }> => {
    const map: Record<string, { itemName: string; unit: string }> = {};
    const catBlocks = this.state.stockItemsByCategory || [];
    const normalizeUnit = (raw: string | undefined): string => {
      if (!raw || typeof raw !== 'string') return '';
      return raw.replace(/^per\s+/i, '').trim();
    };
    catBlocks.forEach((catBlock: any) => {
      (catBlock.subCategories || []).forEach((sub: any) => {
        (sub.items || []).forEach((it: any) => {
          if (it.id) {
            const unit = normalizeUnit(it.itemPriceWithDetails || it.itemUnitDescription || it.itemUnit);
            map[it.id] = { itemName: it.itemName || 'Item', unit };
          }
        });
      });
      (catBlock.itemsWithoutSubCategory || []).forEach((it: any) => {
        if (it.id) {
          const unit = normalizeUnit(it.itemPriceWithDetails || it.itemUnitDescription || it.itemUnit);
          map[it.id] = { itemName: it.itemName || 'Item', unit };
        }
      });
    });
    return map;
  };

  /**
   * Format cart as numbered list without prices, e.g.:
   * 1. Apple - 2 dozen
   * 2. Brijal - 1 kg
   */
  formatShopCartAsMessageText = (): string => {
    const cartEntries = Object.entries(this.state.shopCart)
      .filter(([, q]) => q > 0);
    if (cartEntries.length === 0) return '';
    const itemInfo = this.getShopCartItemInfoMap();
    const lines = cartEntries.map(([stockItemId, quantity], index) => {
      const info = itemInfo[stockItemId] || { itemName: 'Item', unit: '' };
      const name = info.itemName.trim() || 'Item';
      const unitPart = info.unit ? ` ${info.unit}` : '';
      return `${index + 1}. ${name} - ${quantity}${unitPart}`;
    });
    return lines.join('\n');
  };

  /** Shop: share cart in chat – create cart order, then send formatted item list in shopping-request message. */
  handleShareCartInChat = async () => {
    const { shopCart, myShoppingRequest } = this.state;
    const requestId = myShoppingRequest?.id;
    const items = Object.entries(shopCart)
      .filter(([, q]) => q > 0)
      .map(([stockItemId, quantity]) => ({ stockItemId, quantity }));
    if (items.length === 0) {
      this.setState({ shopCartError: 'Add at least one item to the cart.' });
      return;
    }
    if (!requestId) {
      this.setState({ shopCartError: 'Start a chat first (send shopping request and wait for approval).' });
      return;
    }
    this.setState({ isSharingCart: true, shopCartError: null });
    try {
      const res = await apiService.createCartOrder(requestId, { items }) as any;
      const data = (res && res.data !== undefined) ? res.data : res;
      const cartOrderId = data?.cartOrder?.id || '';
      const itemList = this.formatShopCartAsMessageText();
      const messageText = cartOrderId
        ? `<b>Order Id</b>: ${cartOrderId}\n\n${itemList}`
        : itemList;
      await apiService.sendShoppingRequestMessage(requestId, { messageText });
      this.setState({ shopCart: {}, isSharingCart: false, showShoppingRequestChat: true });
    } catch (err: any) {
      const msg = err?.message || 'Failed to share cart. Try again.';
      this.setState({ shopCartError: msg, isSharingCart: false });
    }
  };

  static SHOP_ITEMS_INITIAL = 24;
  static SHOP_ITEMS_MORE = 24;

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Normalize time input to ensure valid format
    let processedValue = value;
    if (name === 'startTime' && value) {
      // Remove any seconds if present (HH:MM:SS -> HH:MM)
      if (value.match(/^\d{2}:\d{2}:\d{2}$/)) {
        processedValue = value.substring(0, 5); // Take only HH:MM
      }
      // Validate hours are 0-23
      const timeParts = processedValue.split(':');
      if (timeParts.length === 2) {
        const hours = parseInt(timeParts[0]);
        if (hours > 23) {
          // Don't allow hours > 23
          return; // Ignore invalid input
        }
      }
    }
    
    this.setState(prevState => ({
      bookingForm: {
        ...prevState.bookingForm,
        [name]: (name === 'numberOfCustomers' || name === 'durationMinutes') 
          ? parseInt(processedValue) || 0 
          : processedValue
      },
      bookingError: null
    }), () => {
      if (name === 'bookingDate' || name === 'startTime' || name === 'durationMinutes') {
        this.fetchChairsStatus();
      }
    });
  };

  handleSubmitBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    const { bookingForm, selectedTable, branch, isJoinMode, joinMainBooking } = this.state;
    
    if (!selectedTable || !branch) {
      this.setState({ 
        bookingError: 'Please select a table and ensure branch information is available.' 
      });
      return;
    }

    // --- Join mode: validate chair, then send join payload ---
    if (isJoinMode) {
      if (!this.state.selectedChairNumber) {
        this.setState({ bookingError: 'Please select a chair to join this table.' });
        return;
      }

      let mainBookingId = joinMainBooking?.id || joinMainBooking?.bookingId || joinMainBooking?.booking_id
        || (selectedTable as any).openForJoinBookingId
        || this.state.openForJoinMap[selectedTable.id];

      if (!mainBookingId) {
        try {
          const openRes = await apiService.getOpenForJoinBookings(branch.id);
          const raw = openRes as any;
          const data = raw?.data ?? raw;
          let bookings: any[] = Array.isArray(data?.bookings) ? data.bookings : (Array.isArray(data) ? data : Array.isArray(raw?.bookings) ? raw.bookings : []);
          if (!bookings.length && data?.booking) bookings = [data.booking];
          const tableIdStr = String(selectedTable.id);
          let match = bookings.find((b: any) => String(b.tableId || b.table?.id || b.table_id || '').trim() === tableIdStr);
          if (!match && bookings.length === 1) match = bookings[0];
          mainBookingId = match?.id || match?.bookingId || match?.booking_id;
        } catch { /* ignore */ }
      }

      this.setState({ isSubmittingBooking: true, bookingError: null, bookingSuccess: false });

      try {
        const joinPayload: any = {
          branchId: branch.id,
          tableId: selectedTable.id,
          chairNumber: this.state.selectedChairNumber,
          allowJoin: true,
          specialRequests: bookingForm.specialRequests?.trim() || undefined
        };
        if (mainBookingId) joinPayload.mainBookingId = mainBookingId;

        const response = await apiService.createBooking(joinPayload);

        if (response && response.success === false) {
          throw new Error(response.message || 'Failed to join booking');
        }

        if (response && response.success && response.data) {
          this.setState({
            bookingSuccess: true,
            isSubmittingBooking: false,
            showBookingModal: false,
            selectedTable: null,
            isJoinMode: false,
            joinMainBooking: null,
            activeBooking: response.data
          });

          const branchId = this.props.params?.branchId;
          if (branchId) this.fetchChairAssignments(branchId);
        } else {
          throw new Error(response?.message || 'Failed to join booking');
        }
      } catch (error: any) {
        console.error('[TableSelection] Join booking failed:', error);
        let errorMessage = 'Failed to join table. Please try again.';
        if (error?.message) errorMessage = error.message;
        this.setState({ isSubmittingBooking: false, bookingError: errorMessage });
      }
      return;
    }

    // --- Normal booking mode ---
    // Validate required fields
    if (!bookingForm.bookingDate || !bookingForm.startTime || !bookingForm.durationMinutes || !bookingForm.numberOfCustomers) {
      this.setState({ 
        bookingError: 'Please fill in all required fields.' 
      });
      return;
    }

    // Validate and normalize time format (ensure HH:MM, not HH:MM:SS)
    let normalizedTime = bookingForm.startTime;
    if (normalizedTime.includes(':')) {
      const timeParts = normalizedTime.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0]);
        const minutes = parseInt(timeParts[1]);
        
        if (hours < 0 || hours > 23) {
          this.setState({ bookingError: 'Invalid time. Hours must be between 00 and 23.' });
          return;
        }
        if (minutes < 0 || minutes > 59) {
          this.setState({ bookingError: 'Invalid time. Minutes must be between 00 and 59.' });
          return;
        }
        normalizedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }

    const bookingDateTime = new Date(`${bookingForm.bookingDate}T${normalizedTime}`);
    if (isNaN(bookingDateTime.getTime())) {
      this.setState({ bookingError: 'Invalid date or time format. Please check your input.' });
      return;
    }
    
    const now = new Date();
    if (bookingDateTime < now) {
      this.setState({ bookingError: 'Cannot book table for a date/time that has already passed.' });
      return;
    }

    if (bookingForm.numberOfCustomers > selectedTable.capacity) {
      this.setState({ 
        bookingError: `Table capacity is ${selectedTable.capacity} customers. Cannot book for ${bookingForm.numberOfCustomers} customers.` 
      });
      return;
    }

    this.setState({ isSubmittingBooking: true, bookingError: null, bookingSuccess: false });

    try {
      let finalTime = normalizedTime;
      if (finalTime.includes(':')) {
        const timeParts = finalTime.split(':');
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0]) || 0;
          const minutes = parseInt(timeParts[1]) || 0;
          if (hours < 0 || hours > 23) {
            this.setState({ isSubmittingBooking: false, bookingError: `Invalid time format. Hours must be between 00 and 23, but got ${hours}.` });
            return;
          }
          if (minutes < 0 || minutes > 59) {
            this.setState({ isSubmittingBooking: false, bookingError: `Invalid time format. Minutes must be between 00 and 59, but got ${minutes}.` });
            return;
          }
          finalTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
      }

      if (!finalTime.match(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)) {
        this.setState({ 
          isSubmittingBooking: false,
          bookingError: `Invalid time format: "${finalTime}". Please select a valid time between 00:00 and 23:59.` 
        });
        return;
      }

      const bookingPayload: any = {
        tableId: selectedTable.id,
        branchId: branch.id,
        bookingDate: bookingForm.bookingDate,
        startTime: finalTime,
        durationMinutes: bookingForm.durationMinutes || 60,
        numberOfCustomers: bookingForm.numberOfCustomers,
        specialRequests: bookingForm.specialRequests?.trim() || undefined
      };
      if (this.state.selectedChairNumber) {
        bookingPayload.chairNumber = this.state.selectedChairNumber;
      }

      console.log('[TableSelection] Submitting booking with validated time:', bookingPayload);

      const response = await apiService.createBooking(bookingPayload);
      
      console.log('[TableSelection] Booking API response:', response);

      // Check if response indicates failure
      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to create booking');
      }

      if (response && response.success && response.data) {
        const bookingData = response.data;
        
        // According to the approval workflow:
        // - Booking status is "requested" (waiting for organizer approval)
        // - Table status remains "available" (NOT reserved until organizer approves)
        // - Table can still be requested by other customers until approved
        // - Only when organizer approves does the table become "reserved"
        
        // Check booking status from response
        const bookingStatus = bookingData.status || 'requested';
        const isRequested = bookingStatus === 'requested' || bookingStatus === 'pending';
        
        console.log('[TableSelection] Booking request submitted successfully.');
        console.log('[TableSelection] Booking status:', bookingStatus);
        console.log('[TableSelection] Booking ID:', bookingData.id);
        console.log('[TableSelection] Table remains available until organizer approves the booking.');
        
        // Show success message and update state
        // Note: We do NOT update table status here - it remains available until organizer approves
        // The table will only become "reserved" when the organizer approves via the organizer portal
        this.setState({ 
          bookingSuccess: true,
          isSubmittingBooking: false,
          showBookingModal: false,
          selectedTable: null,
          activeBooking: bookingData
        });

        // Chat box will not open automatically after booking request
        // Customer can manually open chat if needed
      } else {
        throw new Error(response?.message || 'Failed to create booking');
      }
    } catch (error: any) {
      console.error('[TableSelection] Failed to create booking:', error);
      
      let errorMessage = 'Failed to book table. Please try again.';
      
      // Try to extract error message from various possible error structures
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Check for specific time format errors
      if (errorMessage.toLowerCase().includes('time') || errorMessage.toLowerCase().includes('date')) {
        // If it's a time/date error, provide more helpful guidance
        if (errorMessage.includes('24')) {
          errorMessage = 'Invalid time format. Please select a time between 00:00 and 23:59.';
        }
      }

      this.setState({ 
        isSubmittingBooking: false,
        bookingError: errorMessage
      });
    }
  };

  handleCloseChat = () => {
    this.setState({ showChatBox: false, activeBooking: null });
  };

  // Open the table GROUP chat (organizer + all checked-in customers). Backend enforces
  // membership + occupancy; if the customer has checked out the panel shows a closed state.
  handleOpenTableGroupChat = async (table: Table) => {
    const tableName = (table as any).tableName || ((table as any).tableNumber ? `Table ${(table as any).tableNumber}` : 'Your table');
    let bookingId: string | null = table.assignedBookingId || this.state.activeTableBookingId || null;
    if (!bookingId) {
      try {
        const response = await apiService.getTableBookings(table.id);
        const bookings = (response && response.success && response.data) ? (response.data.bookings || response.data || []) : [];
        const active = bookings.find((b: any) => b.status === 'active' || b.status === 'order_started' || b.status === 'confirmed');
        bookingId = active?.id || null;
      } catch (e) {
        console.error('[TableSelection] Failed to resolve booking for group chat:', e);
      }
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!bookingId || !uuidRegex.test(bookingId)) return;
    this.setState({ showTableGroupChat: true, tableGroupChatBookingId: bookingId, tableGroupChatTableName: tableName });
  };

  handleCloseTableGroupChat = () => {
    this.setState({ showTableGroupChat: false, tableGroupChatBookingId: null, tableGroupChatTableName: null });
  };

  // Open the "Call staff" panel for this table. Resolves the customer's active booking (same rule as
  // the group chat) so the backend can route the call to the staff assigned to this table.
  handleOpenCallStaff = async (table: Table) => {
    const tableName = (table as any).tableName || ((table as any).tableNumber ? `Table ${(table as any).tableNumber}` : 'Your table');
    let bookingId: string | null = table.assignedBookingId || this.state.activeTableBookingId || null;
    if (!bookingId) {
      try {
        const response = await apiService.getTableBookings(table.id);
        const bookings = (response && response.success && response.data) ? (response.data.bookings || response.data || []) : [];
        const active = bookings.find((b: any) => b.status === 'active' || b.status === 'order_started' || b.status === 'confirmed');
        bookingId = active?.id || null;
      } catch (e) {
        console.error('[TableSelection] Failed to resolve booking for call staff:', e);
      }
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!bookingId || !uuidRegex.test(bookingId)) return;
    this.setState((prev) => {
      const callUnreadCounts = { ...prev.callUnreadCounts };
      delete callUnreadCounts[table.id]; // opening the panel clears the unread badge
      return { showCallStaff: true, callStaffBookingId: bookingId!, callStaffTableName: tableName, callUnreadCounts };
    });
  };

  handleCloseCallStaff = () => {
    this.setState({ showCallStaff: false, callStaffBookingId: null, callStaffTableName: null });
  };

  // Open the "View bill / Pay" modal for this table. Resolves the customer's active booking the same
  // way as Call staff so we can load the tab and start a tab payment for the right booking.
  handleOpenBill = async (table: Table) => {
    const tableName = (table as any).tableName || ((table as any).tableNumber ? `Table ${(table as any).tableNumber}` : 'Your table');
    let bookingId: string | null = table.assignedBookingId || this.state.activeTableBookingId || null;
    if (!bookingId) {
      try {
        const response = await apiService.getTableBookings(table.id);
        const bookings = (response && response.success && response.data) ? (response.data.bookings || response.data || []) : [];
        const active = bookings.find((b: any) => b.status === 'active' || b.status === 'order_started' || b.status === 'confirmed');
        bookingId = active?.id || null;
      } catch (e) {
        console.error('[TableSelection] Failed to resolve booking for bill:', e);
      }
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!bookingId || !uuidRegex.test(bookingId)) return;
    this.setState({ showBill: true, billBookingId: bookingId, billTableName: tableName });
  };

  handleCloseBill = () => {
    this.setState({ showBill: false, billBookingId: null, billTableName: null });
  };

  // Open the "Order / Menu" sheet for this table — the same menu-from-inventory order screen QR
  // guests use. Resolves the customer's active booking the same way as Call staff / Bill.
  handleOpenOrder = async (table: Table) => {
    const tableName = (table as any).tableName || ((table as any).tableNumber ? `Table ${(table as any).tableNumber}` : 'Your table');
    let bookingId: string | null = table.assignedBookingId || this.state.activeTableBookingId || null;
    if (!bookingId) {
      try {
        const response = await apiService.getTableBookings(table.id);
        const bookings = (response && response.success && response.data) ? (response.data.bookings || response.data || []) : [];
        const active = bookings.find((b: any) => b.status === 'active' || b.status === 'order_started' || b.status === 'confirmed');
        bookingId = active?.id || null;
      } catch (e) {
        console.error('[TableSelection] Failed to resolve booking for order:', e);
      }
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!bookingId || !uuidRegex.test(bookingId)) return;
    this.setState({ showOrder: true, orderBookingId: bookingId, orderTableName: tableName });
  };

  handleCloseOrder = () => {
    this.setState({ showOrder: false, orderBookingId: null, orderTableName: null });
  };

  // True when this branch is a Game/Sports venue (billiards/pool/snooker/chess/carrom/foosball/darts)
  // — the venues that have a live scoreboard the seated customer can keep.
  isGameVenue = (): boolean => {
    const code = (this.state.branchTypeCode || '').toLowerCase();
    return /games|sport|billiard|pool|snooker|cue|chess|carrom|foosball|foos|dart/.test(code);
  };

  // Open the "Live score" scoreboard modal for this table. Resolves the customer's active booking
  // the same way as Call staff / Order / Bill so the backend can authorize the seated player.
  handleOpenGameScore = async (table: Table) => {
    const tableName = (table as any).tableName || ((table as any).tableNumber ? `Table ${(table as any).tableNumber}` : 'Your table');
    let bookingId: string | null = table.assignedBookingId || this.state.activeTableBookingId || null;
    if (!bookingId) {
      try {
        const response = await apiService.getTableBookings(table.id);
        const bookings = (response && response.success && response.data) ? (response.data.bookings || response.data || []) : [];
        const active = bookings.find((b: any) => b.status === 'active' || b.status === 'order_started' || b.status === 'confirmed');
        bookingId = active?.id || null;
      } catch (e) {
        console.error('[TableSelection] Failed to resolve booking for game score:', e);
      }
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!bookingId || !uuidRegex.test(bookingId)) return;
    this.setState({ showGameScore: true, gameScoreBookingId: bookingId, gameScoreTableId: table.id, gameScoreTableName: tableName });
  };

  handleCloseGameScore = () => {
    this.setState({ showGameScore: false, gameScoreBookingId: null, gameScoreTableId: null, gameScoreTableName: null });
  };

  handleOpenTableChat = async (table: Table) => {
    if (table.isAssignedToYou && table.assignedBookingId) {
      this.setState(prevState => {
        const updatedCounts = { ...prevState.unreadMessageCounts };
        delete updatedCounts[table.id];
        return { activeTableChat: table, activeTableBookingId: table.assignedBookingId!, showChatBox: true, unreadMessageCounts: updatedCounts };
      });
      return;
    }

    // Step 1: Get bookings for the table
    try {
      const response = await apiService.getTableBookings(table.id);
      
      if (response && response.success && response.data) {
        const bookings = response.data.bookings || response.data || [];
        
        // Step 2: Find the active booking (confirmed or active status)
        const activeBooking = bookings.find((booking: any) => 
          booking.status === 'confirmed' || booking.status === 'active'
        );
        
        if (activeBooking && activeBooking.id) {
          // Step 3: Use booking.id (NOT table.id)
          const bookingId = activeBooking.id;
          
          // Validate it's a clean UUID (no prefixes)
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(bookingId)) {
            console.log('[TableSelection] Using booking ID for chat:', bookingId);
            // Clear unread count when opening chat
            this.setState(prevState => {
              const updatedCounts = { ...prevState.unreadMessageCounts };
              if (updatedCounts[table.id]) {
                delete updatedCounts[table.id];
              }
              return {
                activeTableChat: table,
                activeTableBookingId: bookingId, // Store booking ID (NOT table ID)
                showChatBox: true,
                unreadMessageCounts: updatedCounts
              };
            });
            return;
          } else {
            console.warn('[TableSelection] Invalid booking ID format:', bookingId);
          }
        }
      }
      
      // If no active booking found or error, show error
      this.setState({
        socketNotification: {
          show: true,
          type: 'error',
          title: 'Chat Unavailable',
          message: 'No active booking found for this table. Chat is only available for confirmed or active bookings.'
        }
      });
    } catch (error: any) {
      console.error('[TableSelection] Failed to fetch booking for chat:', error);
      this.setState({
        socketNotification: {
          show: true,
          type: 'error',
          title: 'Error',
          message: 'Failed to load booking information. Please try again.'
        }
      });
    }
  };

  handleCloseTableChat = () => {
    this.setState({
      activeTableChat: null,
      activeTableBookingId: null,
      showChatBox: false,
      openChatBookingId: null
    });
    const url = new URL(window.location.href);
    url.searchParams.delete('openChat');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  handleCloseOpenChatFromUrl = () => {
    this.setState({ openChatBookingId: null, showChatBox: false });
    const url = new URL(window.location.href);
    url.searchParams.delete('openChat');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  handleChatNotification = (data: any) => {
    console.log('[TableSelection] Chat notification received:', data);
    
    // Only show notification if chat box is closed
    if (!this.state.showChatBox || this.state.activeTableBookingId !== data.bookingId) {
      const tableId = data.tableId;
      
      if (tableId) {
        // Update unread message count for this table
        this.setState(prevState => {
          const currentCount = prevState.unreadMessageCounts[tableId] || 0;
          const newCount = currentCount + (data.unreadCount || 1);
          
          return {
            unreadMessageCounts: {
              ...prevState.unreadMessageCounts,
              [tableId]: newCount
            }
          };
        });
        
        console.log('[TableSelection] Updated unread count for table:', tableId, 'New count:', this.state.unreadMessageCounts[tableId] + (data.unreadCount || 1));
      }
    }
  };

  handleShowBookingDetails = async (table: Table) => {
    this.setState({ 
      selectedTableForDetails: table,
      showBookingDetailsModal: true,
      isLoadingBookings: true,
      bookingsError: null,
      tableBookings: []
    });

    try {
      const response = await apiService.getTableBookings(table.id);
      
      if (response && response.success && response.data) {
        this.setState({
          tableBookings: response.data.bookings || [],
          isLoadingBookings: false
        });
      } else {
        throw new Error(response?.message || 'Failed to load booking details');
      }
    } catch (error: any) {
      console.error('[TableSelection] Failed to load booking details:', error);
      this.setState({
        isLoadingBookings: false,
        bookingsError: error?.message || 'Failed to load booking details. Please try again.'
      });
    }
  };

  handleCloseBookingDetailsModal = () => {
    this.setState({ 
      showBookingDetailsModal: false,
      selectedTableForDetails: null,
      tableBookings: [],
      bookingsError: null
    });
  };

  formatDateTime = (date: string, time: string) => {
    if (!date || !time) return 'N/A';
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  formatStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'requested': 'Requested',
      'confirmed': 'Confirmed',
      'active': 'Active',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected',
      'no_show': 'No Show'
    };
    return statusMap[status] || status;
  };

  formatPaymentStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Pending',
      'paid': 'Paid',
      'partial': 'Partial',
      'refunded': 'Refunded'
    };
    return statusMap[status] || status;
  };

  handleBackClick = () => {
    const organizerId = this.props.params?.organizerId;
    const branchId = this.props.params?.branchId;
    // Back to this branch's hub (Tables / Rooms / Tournaments), not the branch list.
    if (branchId) {
      this.props.navigate(`/customer/organizers/${organizerId}/branches/${branchId}`);
    } else {
      this.props.navigate(`/customer/organizers/${organizerId}`);
    }
  };

  getStatusCounts = () => {
    const areas = this.state.areas ?? [];
    const allTables = (Array.isArray(areas) ? areas : []).flatMap(awt => awt?.tables ?? []);
    return {
      available: allTables.filter(t => t?.status === 'available').length,
      occupied: allTables.filter(t => t?.status === 'occupied').length,
      reserved: allTables.filter(t => t?.status === 'reserved').length,
      maintenance: allTables.filter(t => t?.status === 'maintenance').length
    };
  };

  getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return '#10b981'; // Green
      case 'occupied': return '#f59e0b'; // Orange
      case 'reserved': return '#3b82f6'; // Blue
      case 'maintenance': return '#ef4444'; // Red
      default: return '#6b7280';
    }
  };

  getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'occupied': return 'Occupied';
      case 'reserved': return 'Reserved';
      case 'maintenance': return 'Maintenance';
      default: return status;
    }
  };

  // Salon / barber / spa venues seat one person per chair — a "table" here IS a single chair.
  isChairVenue = () => branchTypeTheme({ code: this.state.branchTypeCode }).spot === 'Chair';

  // Is this the customer's own table (booked / reserved-for / checked-in / assigned)?
  isMyTable = (table: Table) => !!(table.isTableYours || table.isBookedByYou || table.isAssignedToYou);
  // Does the customer currently hold any table in this branch? (Then dull the others until checkout.)
  hasMyActiveTable = () => (this.state.areas || []).some((awt: any) => (awt.tables || []).some((t: Table) => this.isMyTable(t)));

  getTableDisplayStatus = (table: Table) => {
    if (table.isBookedByYou && table.isTableYours) {
      return 'Reserved For You';
    } else if (table.isBookedByYou && !table.isTableYours) {
      return 'Booked By You';
    } else if (table.isAssignedToYou) {
      return 'Assigned to You';
    } else if (table.seatRequestStatus === 'pending') {
      return 'Seat Requested';
    } else if (table.isBookingOpened && !table.isTableYours) {
      return 'Open for Join';
    }
    return this.getStatusLabel(table.status);
  };

  /** First table from first area when branch is grocery (represents the "shop" for shopping request). */
  getGroceryTable = (): Table | null => {
    if (this.state.branchTypeCode !== 'grocery' || !this.state.areas.length) return null;
    const firstArea = this.state.areas[0];
    const tables = firstArea?.tables ?? [];
    return tables.length > 0 ? tables[0] : null;
  };

  render() {
    const { branch, areas, organizer, selectedTable, showBookingModal, bookingForm, isLoading, error, socketNotification } = this.state;

    if (isLoading) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.tableSelectionContainer}>
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading tables...</p>
            </div>
          </div>
          <Footer />
        </>
      );
    }

    if (!branch) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.tableSelectionContainer}>
            <div className={styles.errorState}>
              <h2>Branch not found</h2>
              <button onClick={this.handleBackClick} className={styles.backButton}>
                ← Back
              </button>
            </div>
          </div>
          <Footer />
        </>
      );
    }

    const statusCounts = this.getStatusCounts();
    const isGroceryBranch = this.state.branchTypeCode === 'grocery';

    return (
      <>
        <DashboardHeader />
        {/* Venue-wide staff announcements (pushed to all tables). */}
        <div style={{ padding: '0 16px', marginTop: 10 }}>
          <BranchBroadcasts branchId={this.props.params?.branchId || this.state.branch?.id} />
        </div>
        {/* Socket Notification */}
        {socketNotification && socketNotification.show && (
          <div className={`${styles.socketNotification} ${styles[socketNotification.type]}`}>
            <div className={styles.notificationContent}>
              <div className={styles.notificationIcon}>
                {socketNotification.type === 'success' && '✅'}
                {socketNotification.type === 'error' && '❌'}
                {socketNotification.type === 'info' && 'ℹ️'}
              </div>
              <div className={styles.notificationText}>
                <div className={styles.notificationTitle}>{socketNotification.title}</div>
                <div className={styles.notificationMessage}>{socketNotification.message}</div>
              </div>
              <button 
                className={styles.notificationClose}
                onClick={this.handleCloseNotification}
                aria-label="Close notification"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <div className={styles.tableSelectionContainer}>
          <div className={styles.tableSelectionContent}>
            {/* Back Button */}
            <button onClick={this.handleBackClick} className={styles.backButton}>
              ← Back
            </button>

            {/* Branch Header */}
            <div className={styles.branchHeader}>
              <div className={styles.branchHeaderInfo}>
                <h1 className={styles.branchName}>{branch.name}</h1>
                <p className={styles.branchAddress}>{branch.address}</p>
              </div>
              {!isGroceryBranch && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <WaitlistJoinButton branchId={branch.id} branchName={branch.name} />
                  <button
                    className={styles.tournamentsButton}
                    onClick={() => {
                      const organizerId = this.props.params?.organizerId;
                      this.props.navigate(`/customer/organizers/${organizerId}/branches/${branch.id}/tournaments`);
                    }}
                  >
                    🏆 View Tournaments
                  </button>
                </div>
              )}
            </div>

            {!isGroceryBranch && this.state.hasActiveBooking && (
              <div className={styles.errorMessage} style={{ marginBottom: '1rem' }}>
                <span>ℹ️ You already have an active booking at this venue. Complete or cancel it to book another {branchTypeTheme({ code: this.state.branchTypeCode }).spot.toLowerCase()} here.</span>
              </div>
            )}

            {/* Grocery branch: menu-style design – shopping request (no table) */}
            {isGroceryBranch ? (
              <div className={styles.groceryView}>
                <div className={styles.groceryViewHeader}>
                  <h2 className={styles.groceryViewTitle}>Grocery Store</h2>
                  <p className={styles.groceryViewSubtitle}>Browse and shop at {branch.name}</p>
                </div>
                {this.state.bookingError && (
                  <div className={styles.errorMessage} style={{ marginBottom: '1rem' }}>
                    <span>⚠️ {this.state.bookingError}</span>
                  </div>
                )}
                <div className={styles.groceryMenu}>
                  <section className={styles.groceryMenuSection}>
                    <h3 className={styles.groceryMenuSectionTitle}>Request & chat</h3>
                    <ul className={styles.groceryMenuList}>
                      <li className={styles.groceryMenuItem}>
                        <span className={styles.groceryMenuItemIcon} aria-hidden>🛒</span>
                        <div className={styles.groceryMenuItemContent}>
                          <span className={styles.groceryMenuItemLabel}>Send shopping request</span>
                          <span className={styles.groceryMenuItemDesc}>
                            {!this.state.myShoppingRequest
                              ? 'Request to shop at this store. Once accepted, you can chat with the store.'
                              : this.state.myShoppingRequest.status === 'requested'
                                ? 'Your request is pending. Store will accept soon.'
                                : ['confirmed', 'active'].includes(this.state.myShoppingRequest.status)
                                  ? 'Your request was accepted. You can chat with the store.'
                                  : `Status: ${this.state.myShoppingRequest.status}.`}
                          </span>
                        </div>
                        <div className={styles.groceryMenuItemActions}>
                          {!this.state.myShoppingRequest && (
                            <button
                              type="button"
                              className={styles.groceryMenuButton}
                              onClick={() => this.handleGrocerySendRequest()}
                              disabled={this.state.isSubmittingBooking}
                            >
                              {this.state.isSubmittingBooking ? 'Sending...' : 'Send request'}
                            </button>
                          )}
                          {this.state.myShoppingRequest && ['requested', 'confirmed', 'active'].includes(this.state.myShoppingRequest.status) && (
                            <>
                              <button
                                type="button"
                                className={styles.groceryMenuButton}
                                onClick={this.handleOpenShoppingRequestChat}
                              >
                                💬 Chat
                                {(this.state.shoppingRequestUnreadCount ?? 0) > 0 && (
                                  <span className={styles.groceryMenuBadge} aria-label={`${this.state.shoppingRequestUnreadCount ?? 0} unread messages`}>
                                    {(this.state.shoppingRequestUnreadCount ?? 0) > 99 ? '99+' : (this.state.shoppingRequestUnreadCount ?? 0)}
                                  </span>
                                )}
                              </button>
                              <button
                                type="button"
                                className={styles.groceryMenuButtonSecondary}
                                onClick={() => this.state.branch?.id && this.props.navigate(`/cart-orders?branchId=${encodeURIComponent(this.state.branch.id)}`)}
                              >
                                My orders
                              </button>
                            </>
                          )}
                          {this.state.myShoppingRequest && ['completed', 'rejected', 'cancelled'].includes(this.state.myShoppingRequest.status) && (
                            <span className={styles.groceryMenuItemDesc}>Request {this.state.myShoppingRequest.status}.</span>
                          )}
                        </div>
                      </li>
                    </ul>
                  </section>

                  {/* Cart summary: show when cart has items; Share in chat creates cart order and sends link */}
                  {(() => {
                    const cartEntries = Object.entries(this.state.shopCart).filter(([, q]) => q > 0);
                    if (cartEntries.length === 0) return null;
                    const itemMap: Record<string, { itemName: string; itemPrice: number }> = {};
                    (this.state.stockItemsByCategory || []).forEach((catBlock: any) => {
                      (catBlock.subCategories || []).forEach((sub: any) => {
                        (sub.items || []).forEach((it: any) => { if (it.id) itemMap[it.id] = { itemName: it.itemName || 'Item', itemPrice: Number(it.itemPrice) || 0 }; });
                      });
                      (catBlock.itemsWithoutSubCategory || []).forEach((it: any) => { if (it.id) itemMap[it.id] = { itemName: it.itemName || 'Item', itemPrice: Number(it.itemPrice) || 0 }; });
                    });
                    let subtotal = 0;
                    return (
                      <section className={styles.shopCartSection}>
                        <h3 className={styles.shopCartSectionTitle}>Your cart</h3>
                        <ul className={styles.shopCartList}>
                          {cartEntries.map(([stockItemId, quantity]) => {
                            const info = itemMap[stockItemId] || { itemName: 'Item', itemPrice: 0 };
                            const lineTotal = info.itemPrice * quantity;
                            subtotal += lineTotal;
                            return (
                              <li key={stockItemId} className={styles.shopCartItem}>
                                <span className={styles.shopCartItemName}>{info.itemName} × {quantity}</span>
                                <span className={styles.shopCartItemPrice}>₹{lineTotal}</span>
                                <button type="button" className={styles.shopCartItemRemove} onClick={() => this.handleShopRemoveFromCart(stockItemId, true)} aria-label="Remove">×</button>
                              </li>
                            );
                          })}
                        </ul>
                        <p className={styles.shopCartSubtotal}>Subtotal: ₹{subtotal}</p>
                        {this.state.shopCartError && <p className={styles.shopCartError}>{this.state.shopCartError}</p>}
                        <div className={styles.shopCartActions}>
                          <button type="button" className={styles.shopCartClearBtn} onClick={this.handleShopClearCart}>Clear cart</button>
                          <button type="button" className={styles.shopCartShareBtn} onClick={this.handleShareCartInChat} disabled={this.state.isSharingCart}>
                            {this.state.isSharingCart ? 'Sharing...' : 'Share in chat'}
                          </button>
                        </div>
                      </section>
                    );
                  })()}
                </div>

                {/* Shop: stepped navigation – Category → Subcategory → Items */}
                {this.state.stockItemsByCategory && this.state.stockItemsByCategory.length > 0 && (() => {
                  const searchQ = this.state.shopSearchQuery;
                  const visibleCount = this.state.shopVisibleCount;
                  const catId = this.state.shopSelectedCategoryId;
                  const subId = this.state.shopSelectedSubCategoryId;
                  const ITEMS_INITIAL = TableSelection.SHOP_ITEMS_INITIAL;
                  const matchText = (text: string) => !searchQ || (text || '').toLowerCase().includes(searchQ);
                  const matchItem = (item: any) => !searchQ || (item.itemName || '').toLowerCase().includes(searchQ);
                  const sortedCategories = this.state.stockItemsByCategory
                    .slice()
                    .sort((a: any, b: any) => (a.category?.displayOrder ?? 999) - (b.category?.displayOrder ?? 999));

                  const selectedCatBlock = catId ? sortedCategories.find((c: any) => (c.category?.id || c.category?.categoryName) === catId) : null;
                  const selectedCat = selectedCatBlock?.category || {};
                  const subCats = selectedCatBlock?.subCategories || [];
                  const itemsWithoutSub = (selectedCatBlock?.itemsWithoutSubCategory || []).filter(matchItem);
                  const selectedSubBlock = subId ? subCats.find((s: any) => (s.subCategory?.id || s.subCategory?.subCategoryName) === subId) : null;
                  const selectedSub = selectedSubBlock?.subCategory || {};
                  const currentItems = selectedSubBlock ? (selectedSubBlock.items || []).filter(matchItem) : itemsWithoutSub;

                  return (
                    <section className={styles.shopSection}>
                      <h3 className={styles.shopSectionTitle}>Browse products</h3>
                      <input
                        type="search"
                        placeholder="Search..."
                        value={this.state.shopSearchQuery}
                        onChange={this.handleShopSearchChange}
                        className={styles.shopSearchInput}
                        aria-label="Search products"
                      />

                      {/* Step 1: No category selected – show categories */}
                      {!catId && (
                        <div className={styles.shopStep}>
                          <p className={styles.shopStepLabel}>Select a category</p>
                          <div className={styles.shopCategoryGrid}>
                            {sortedCategories.filter((catBlock: any) => {
                              const c = catBlock.category || {};
                              return matchText(c.categoryName);
                            }).map((catBlock: any) => {
                              const c = catBlock.category || {};
                              const id = c.id || c.categoryName;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  className={styles.shopCategoryCard}
                                  onClick={() => this.handleShopSelectCategory(id)}
                                >
                                  {c.categoryImage && <img src={c.categoryImage} alt="" className={styles.shopCategoryCardImage} />}
                                  <span className={styles.shopCategoryCardName}>{c.categoryName || 'Category'}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Step 2: Category selected, no sub – show subcategories */}
                      {catId && !subId && (
                        <div className={styles.shopStep}>
                          <button type="button" className={styles.shopBackBtn} onClick={this.handleShopBackToCategories}>
                            ← All categories
                          </button>
                          <p className={styles.shopStepLabel}>{(selectedCat as any).categoryName || 'Category'} – Select subcategory</p>
                          <div className={styles.shopSubCategoryGrid}>
                            {subCats.filter((subBlock: any) => {
                              const s = subBlock.subCategory || {};
                              return matchText(s.subCategoryName) && (subBlock.items || []).length > 0;
                            }).map((subBlock: any) => {
                              const s = subBlock.subCategory || {};
                              const id = s.id || s.subCategoryName;
                              const count = (subBlock.items || []).length;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  className={styles.shopSubCategoryCard}
                                  onClick={() => this.handleShopSelectSubCategory(id)}
                                >
                                  {s.subCategoryImage && <img src={s.subCategoryImage} alt="" className={styles.shopSubCategoryCardImage} />}
                                  <span className={styles.shopSubCategoryCardName}>{s.subCategoryName}</span>
                                  <span className={styles.shopSubCategoryCardCount}>{count} items</span>
                                </button>
                              );
                            })}
                            {itemsWithoutSub.length > 0 && (
                              <button
                                type="button"
                                className={styles.shopSubCategoryCard}
                                onClick={() => this.handleShopSelectSubCategory('no-sub')}
                              >
                                <span className={styles.shopSubCategoryCardName}>Other</span>
                                <span className={styles.shopSubCategoryCardCount}>{itemsWithoutSub.length} items</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Step 3: Category + sub selected – show items */}
                      {catId && subId && (
                        <div className={styles.shopStep}>
                          <div className={styles.shopBackRow}>
                            <button type="button" className={styles.shopBackBtn} onClick={this.handleShopBackToSubCategories}>
                              ← {(selectedCat as any).categoryName || 'Category'}
                            </button>
                            <button type="button" className={styles.shopBackBtn} onClick={this.handleShopBackToCategories}>
                              All categories
                            </button>
                          </div>
                          <p className={styles.shopStepLabel}>
                            {(selectedCat as any).categoryName} › {subId === 'no-sub' ? 'Other' : ((selectedSub as any).subCategoryName || '')}
                          </p>
                          {(() => {
                            const key = subId === 'no-sub' ? `${catId}-no-sub` : `${catId}-${subId}`;
                            const limit = visibleCount[key] ?? ITEMS_INITIAL;
                            const visible = currentItems.slice(0, limit);
                            const hasMore = currentItems.length > limit;
                            return (
                              <>
                                <div className={styles.shopProductGrid}>
                                  {visible.map((item: any) => {
                                    const qty = this.state.shopCart[item.id] || 0;
                                    return (
                                      <article key={item.id} className={styles.shopProductCard}>
                                        <div className={styles.shopProductImageWrap}>
                                          {item.itemImage ? (
                                            <img src={item.itemImage} alt={item.itemName} className={styles.shopProductImage} />
                                          ) : (
                                            <div className={styles.shopProductImagePlaceholder}>📦</div>
                                          )}
                                        </div>
                                        <div className={styles.shopProductInfo}>
                                          <h6 className={styles.shopProductName}>{item.itemName}</h6>
                                          <p className={styles.shopProductPrice}>
                                            ₹{Number(item.itemPrice) ?? 0}
                                            {(item.itemPriceWithDetails || item.itemUnitDescription) && (
                                              <span className={styles.shopProductUnit}> / {item.itemPriceWithDetails || item.itemUnitDescription}</span>
                                            )}
                                          </p>
                                          <div className={styles.shopProductCartRow}>
                                            {qty === 0 ? (
                                              <button type="button" className={styles.shopProductAddBtn} onClick={() => this.handleShopAddToCart(item.id)}>
                                                Add to cart
                                              </button>
                                            ) : (
                                              <>
                                                <button type="button" className={styles.shopProductQtyBtn} onClick={() => this.handleShopRemoveFromCart(item.id)} aria-label="Decrease">−</button>
                                                <span className={styles.shopProductQty}>{qty}</span>
                                                <button type="button" className={styles.shopProductQtyBtn} onClick={() => this.handleShopAddToCart(item.id)} aria-label="Increase">+</button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </article>
                                    );
                                  })}
                                </div>
                                {hasMore && (
                                  <button type="button" className={styles.shopShowMoreBtn} onClick={() => this.handleShopShowMore(key)}>
                                    Show more ({currentItems.length - limit} more)
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </section>
                  );
                })()}
              </div>
            ) : (
              <>
            {/* Status Summary */}
            <div className={styles.statusSummary}>
              <div className={styles.statusItem}>
                <span className={styles.statusDot} style={{ backgroundColor: '#10b981' }}></span>
                <span>Available: {statusCounts.available}</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusDot} style={{ backgroundColor: '#f59e0b' }}></span>
                <span>Occupied: {statusCounts.occupied}</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusDot} style={{ backgroundColor: '#3b82f6' }}></span>
                <span>Reserved: {statusCounts.reserved}</span>
              </div>
              <div className={styles.statusItem}>
                <span className={styles.statusDot} style={{ backgroundColor: '#ef4444' }}></span>
                <span>Maintenance: {statusCounts.maintenance}</span>
              </div>
            </div>

            {/* Bar Layout View */}
            <div className={styles.barLayoutContainer}>
              <div className={styles.barLayoutHeader}>
                <div className={styles.barLayoutHeaderText}>
                  <h2 className={styles.layoutTitle}>{branchTypeTheme({ code: this.state.branchTypeCode }).spots} Layout</h2>
                  <p className={styles.layoutSubtitle}>Click on an available {branchTypeTheme({ code: this.state.branchTypeCode }).spot.toLowerCase()} to book</p>
                </div>
                {!isGroceryBranch && this.branchHasFeature('song_requests') && (
                  <button
                    type="button"
                    className={styles.liveSongRequestHeaderButton}
                    onClick={() => this.setState({ showLiveSongModal: true })}
                  >
                    🎵 Live Song Request
                  </button>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className={styles.errorMessage}>
                  <span>⚠️ {error}</span>
                </div>
              )}

              {/* Visual Bar Layout - Grouped by Areas */}
              <div className={styles.barFloorPlan}>
                {areas.length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyStateIcon}>🪑</div>
                    <h3>No tables available</h3>
                    <p>This branch doesn't have any tables configured yet.</p>
                  </div>
                ) : (
                  areas.map((areaWithTables) => {
                    const { area, tables: areaTables } = areaWithTables;
                    
                    // Log for debugging
                    if (areaTables.length === 0) {
                      console.warn(`[TableSelection] Area "${area.name}" has no tables`);
                    }
                    
                    // Skip inactive areas or areas with no tables
                    if (!area.isActive || areaTables.length === 0) return null;

                    return (
                      <div key={area.id} className={styles.locationSection}>
                        <div className={styles.areaHeader}>
                          <h3 className={styles.locationTitle}>{area.name}</h3>
                          {area.description && (
                            <p className={styles.areaDescription}>{area.description}</p>
                          )}
                        </div>
                        <div className={styles.tablesInLocation}>
                          {areaTables.map(table => (
                            <div
                              key={table.id}
                              className={`${styles.tableItem} ${
                                table.isAssignedToYou
                                  ? styles.tableAssigned
                                  : table.seatRequestStatus === 'pending'
                                  ? styles.tableSeatRequested
                                  : table.isBookingOpened && !table.isTableYours
                                  ? styles.tableBookingOpen
                                  : table.status === 'available'
                                  ? styles.tableAvailable
                                  : table.status === 'occupied'
                                  ? styles.tableOccupied
                                  : table.status === 'reserved'
                                  ? styles.tableReserved
                                  : styles.tableMaintenance
                              } ${this.isMyTable(table) ? styles.tableMine : (this.hasMyActiveTable() ? styles.tableDulled : '')}`}
                              onClick={() => {
                                if (table.isAssignedToYou) return;
                                if (table.seatRequestStatus === 'pending') return;
                                if (table.isBookingOpened && !table.isTableYours && !table.isAssignedToYou) {
                                  this.handleTableClick(table);
                                  return;
                                }
                                if (table.status === 'available' && !table.isBookedByYou) {
                                  this.handleTableClick(table);
                                }
                              }}
                              title={`${(table as any).tableName || `Table ${table.number}`} - ${this.getTableDisplayStatus(table)} - Capacity: ${table.capacity}`}
                            >
                              <div className={styles.tableContent} style={(table.status === 'occupied' || table.status === 'reserved' || table.status === 'maintenance') && !table.isTableYours && !table.isAssignedToYou && !table.isBookedByYou ? { paddingBottom: '1.25rem' } : undefined}>
                                <div className={styles.tableIconWrap} aria-hidden style={{ width: '100%', height: 90, borderRadius: 10, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {(table as any).imageUrl ? (
                                    <img src={(table as any).imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : this.isChairVenue() ? (
                                    // Salon / barber chair (side view): backrest, headrest, seat, armrest, hydraulic base, footrest.
                                    <svg className={styles.tableIcon} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="12" y="6" width="9" height="22" rx="4" fill="currentColor" opacity="0.9"/>
                                      <rect x="9" y="4" width="6" height="9" rx="3" fill="currentColor" opacity="0.7"/>
                                      <rect x="12" y="24" width="24" height="7" rx="3" fill="currentColor" opacity="0.9"/>
                                      <rect x="30" y="19" width="6" height="9" rx="3" fill="currentColor" opacity="0.7"/>
                                      <rect x="21" y="31" width="4" height="8" fill="currentColor" opacity="0.8"/>
                                      <rect x="15" y="39" width="16" height="4" rx="2" fill="currentColor" opacity="0.6"/>
                                      <rect x="32" y="33" width="9" height="4" rx="2" fill="currentColor" opacity="0.6"/>
                                    </svg>
                                  ) : (
                                    <svg className={styles.tableIcon} viewBox="0 0 64 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="4" y="2" width="56" height="28" rx="4" fill="currentColor" opacity="0.9"/>
                                      <circle cx="8" cy="8" r="2.5" fill="currentColor" opacity="0.4"/>
                                      <circle cx="56" cy="8" r="2.5" fill="currentColor" opacity="0.4"/>
                                      <circle cx="8" cy="24" r="2.5" fill="currentColor" opacity="0.4"/>
                                      <circle cx="56" cy="24" r="2.5" fill="currentColor" opacity="0.4"/>
                                      <circle cx="32" cy="16" r="3" fill="currentColor" opacity="0.5"/>
                                    </svg>
                                  )}
                                </div>
                                <div className={styles.tableNumberBadge}>{(table as any).tableName || `${this.isChairVenue() ? 'C' : 'T'}${table.number}`}</div>
                                {!this.isChairVenue() && (
                                  <div className={styles.tableCapacityBadge}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                      <circle cx="9" cy="7" r="4"></circle>
                                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                    <span>{table.capacity}</span>
                                  </div>
                                )}
                                <div className={styles.tableStatusBadge}>
                                  {this.getTableDisplayStatus(table)}
                                </div>
                              </div>
                              {(table.isTableYours || table.isAssignedToYou || table.isBookedByYou) && (
                                <div className={styles.tableActions}>
                                  {(table.isTableYours || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenTableChat(table);
                                      }}
                                      title="Open chat for this table"
                                    >
                                      <span className={styles.chatButtonContent}>
                                        💬 Chat
                                        {this.state.unreadMessageCounts[table.id] > 0 && (
                                          <span className={styles.chatNotificationBadge}>
                                            {this.state.unreadMessageCounts[table.id] > 99 ? '99+' : this.state.unreadMessageCounts[table.id]}
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                  )}
                                  {(table.isTableYours || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenTableGroupChat(table);
                                      }}
                                      title="Open group chat & order from the menu"
                                    >
                                      <span className={styles.chatButtonContent}>👥 Group & Order</span>
                                    </button>
                                  )}
                                  {(((table as any).isCheckedIn && table.isTableYours) || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      style={{ background: '#16a34a', borderColor: '#16a34a', color: '#ffffff' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenCallStaff(table);
                                      }}
                                      title="Call staff to your table"
                                    >
                                      <span className={styles.chatButtonContent} style={{ color: '#ffffff' }}>
                                        🔔 Call staff
                                        {this.state.callUnreadCounts[table.id] > 0 && (
                                          <span className={styles.chatNotificationBadge}>
                                            {this.state.callUnreadCounts[table.id] > 99 ? '99+' : this.state.callUnreadCounts[table.id]}
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                  )}
                                  {(((table as any).isCheckedIn && table.isTableYours) || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenOrder(table);
                                      }}
                                      title="See the menu and order to your table"
                                    >
                                      <span className={styles.chatButtonContent}>🍔 Order / Menu</span>
                                    </button>
                                  )}
                                  {this.isGameVenue() && (((table as any).isCheckedIn && table.isTableYours) || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenGameScore(table);
                                      }}
                                      title="See and keep the live game score"
                                    >
                                      <span className={styles.chatButtonContent}>🎯 Live score</span>
                                    </button>
                                  )}
                                  {(((table as any).isCheckedIn && table.isTableYours) || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableChatButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleOpenBill(table);
                                      }}
                                      title="View your bill and pay"
                                    >
                                      <span className={styles.chatButtonContent}>🧾 View bill / Pay</span>
                                    </button>
                                  )}
                                  {(table.isBookedByYou || table.isTableYours || table.isAssignedToYou) && (
                                    <button
                                      className={styles.tableBookingDetailsButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.handleShowBookingDetails(table);
                                      }}
                                      title="Show booking details"
                                    >
                                      📋 Booking Details
                                    </button>
                                  )}
                                </div>
                              )}
                              {table.status === 'available'
                                && !table.isBookedByYou
                                && !table.isTableYours
                                && !table.isAssignedToYou
                                && !table.isBookingOpened
                                && !this.hasMyActiveTable() && (
                                <div className={styles.tableActions}>
                                  <button
                                    className={styles.tableChatButton}
                                    disabled={this.state.imHereTableId === table.id}
                                    style={{ background: '#e0531f', borderColor: '#e0531f', color: '#ffffff' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.handleImHere(table);
                                    }}
                                    title="You're at the venue now — one tap tells staff to seat you here (they confirm you in)"
                                  >
                                    <span className={styles.chatButtonContent} style={{ color: '#ffffff' }}>
                                      {this.state.imHereTableId === table.id ? 'Sending…' : "🙋 I'm here — seat me"}
                                    </span>
                                  </button>
                                  <button
                                    className={styles.tableChatButton}
                                    style={{ background: '#16a34a', borderColor: '#16a34a', color: '#ffffff' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.handleRequestTable(table);
                                    }}
                                    title="Request this table — fill in the details; staff will review and confirm"
                                  >
                                    <span className={styles.chatButtonContent} style={{ color: '#ffffff' }}>
                                      📩 Request this table
                                    </span>
                                  </button>
                                </div>
                              )}
                              {table.status === 'available' && (
                                <div className={styles.tablePulse}></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Legend */}
              <div className={styles.legend}>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendDot} ${styles.legendAvailable}`}></div>
                  <span>Available</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendDot} ${styles.legendOccupied}`}></div>
                  <span>Occupied</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendDot} ${styles.legendReserved}`}></div>
                  <span>Reserved</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendDot} ${styles.legendMaintenance}`}></div>
                  <span>Maintenance</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendDot} ${styles.legendBookingOpen}`}></div>
                  <span>Open for Join</span>
                </div>
              </div>
            </div>

            {/* Booking Modal */}
            {this.state.showRequestSeatModal && this.state.requestSeatTable && (
              <div className={styles.modalOverlay} onClick={this.handleCloseRequestSeatModal}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h2>Request to Join Table</h2>
                    <p className={styles.modalSubtitle}>
                      {(this.state.requestSeatTable as any).tableName || `Table ${this.state.requestSeatTable.number}`} &bull; Capacity: {this.state.requestSeatTable.capacity}
                    </p>
                    <button className={styles.modalClose} onClick={this.handleCloseRequestSeatModal}>&times;</button>
                  </div>
                  <div className={styles.bookingForm}>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
                      This table has open booking. You can request a seat and the organizer will review your request. Once approved, you'll be assigned to a chair and get access to all table features.
                    </p>
                    {this.state.requestSeatError && (
                      <div className={styles.bookingErrorMessage}>
                        {this.state.requestSeatError}
                      </div>
                    )}
                    <div className={styles.formActions}>
                      <button
                        className={styles.cancelButton}
                        onClick={this.handleCloseRequestSeatModal}
                        disabled={this.state.isRequestingSeat}
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.submitButton}
                        onClick={this.handleRequestSeat}
                        disabled={this.state.isRequestingSeat}
                      >
                        {this.state.isRequestingSeat ? 'Requesting...' : 'Request to Join'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showBookingModal && selectedTable && (
              <div className={styles.modalOverlay} onClick={this.handleCloseModal}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h2>
                      {this.state.isJoinMode
                        ? `Join Table ${(selectedTable as any).tableName || (selectedTable as any).tableNumber || `T${selectedTable.number}`}`
                        : this.state.branchTypeCode === 'grocery'
                        ? 'Send shopping request'
                        : `Book ${branchTypeTheme({ code: this.state.branchTypeCode }).spot} ${(selectedTable as any).tableName || (selectedTable as any).tableNumber || `T${selectedTable.number}`}`}
                    </h2>
                    <p className={styles.modalSubtitle}>
                      {this.state.isJoinMode
                        ? 'Select an available chair to join this table'
                        : this.state.branchTypeCode === 'grocery'
                        ? 'Request to shop at this store. Once the organizer accepts, you can chat.'
                        : 'Reserve a table for dining and drinks'}
                    </p>
                    <button className={styles.modalClose} onClick={this.handleCloseModal}>×</button>
                  </div>

                  {(selectedTable as any).imageUrl && (
                    <img src={(selectedTable as any).imageUrl} alt="Where this table is" style={{ width: '100%', height: 170, objectFit: 'cover', borderRadius: 12, margin: '0 0 12px' }} />
                  )}

                  <form onSubmit={this.handleSubmitBooking} className={styles.bookingForm}>
                    {/* Error Message */}
                    {this.state.bookingError && (
                      <div className={styles.bookingErrorMessage}>
                        <span>⚠️ {this.state.bookingError}</span>
                      </div>
                    )}

                    {/* Success Message */}
                    {this.state.bookingSuccess && (
                      <div className={styles.bookingSuccessMessage}>
                        <span>
                          {this.state.isJoinMode
                            ? '✅ Join request sent successfully! Waiting for organizer approval.'
                            : this.state.branchTypeCode === 'grocery'
                            ? '✅ Shopping request submitted successfully! Waiting for organizer approval. You can chat once they accept.'
                            : '✅ Booking request submitted successfully! Your request is now waiting for organizer approval. You can chat with the organizer about your request.'}
                        </span>
                      </div>
                    )}

                    {!this.state.isJoinMode && (
                      <>
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label>Number of Customers *</label>
                        <select
                          name="numberOfCustomers"
                          value={bookingForm.numberOfCustomers}
                          onChange={this.handleInputChange}
                          required
                        >
                          {Array.from({ length: selectedTable?.capacity || 20 }, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                        {selectedTable && (
                          <small className={styles.formHint}>
                            {this.state.branchTypeCode === 'grocery' ? 'Number of people' : `Table capacity: ${selectedTable.capacity} customers`}
                          </small>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label>Duration (Minutes) *</label>
                        <select
                          name="durationMinutes"
                          value={bookingForm.durationMinutes}
                          onChange={this.handleInputChange}
                          required
                        >
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value={90}>1.5 hours</option>
                          <option value={120}>2 hours</option>
                          <option value={180}>3 hours</option>
                          <option value={240}>4 hours</option>
                        </select>
                      </div>
                    </div>

                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label>Booking Date *</label>
                        <input
                          type="date"
                          name="bookingDate"
                          value={bookingForm.bookingDate}
                          onChange={this.handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>

                      <div className={styles.formGroup}>
                        <label>Start Time *</label>
                        <input
                          type="time"
                          name="startTime"
                          value={bookingForm.startTime}
                          onChange={this.handleInputChange}
                          min="00:00"
                          max="23:59"
                          step="60"
                          required
                        />
                        <small className={styles.formHint}>
                          Select time between 00:00 and 23:59
                        </small>
                      </div>
                    </div>
                      </>
                    )}

                    <div className={styles.formGroup}>
                      <label>Special Requests (Optional)</label>
                      <textarea
                        name="specialRequests"
                        value={bookingForm.specialRequests}
                        onChange={this.handleInputChange}
                        rows={3}
                        placeholder="Any special requests or preferences..."
                      />
                    </div>

                    {/* Chair Picker — in join mode always; in normal mode only when chair 1 is NOT available */}
                    {this.state.branchTypeCode !== 'grocery' && (() => {
                      const chair1Available = this.state.chairsStatus.some((c: any) => (c.chairNumber ?? c.chair_number) === 1 && c.status === 'available');
                      return this.state.isJoinMode || ((bookingForm.bookingDate && bookingForm.startTime) && !chair1Available);
                    })() && (
                      <div className={styles.formGroup}>
                        <label>Select Your Chair</label>
                        {this.state.isLoadingChairs ? (
                          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading chairs...</p>
                        ) : this.state.chairsStatus.length > 0 ? (
                          <>
                            <div className={styles.chairGrid}>
                              {this.state.chairsStatus.map((chair) => (
                                <button
                                  key={chair.chairNumber}
                                  type="button"
                                  className={`${styles.chairItem} ${
                                    chair.status === 'available'
                                      ? this.state.selectedChairNumber === chair.chairNumber
                                        ? styles.chairSelected
                                        : styles.chairAvailable
                                      : chair.status === 'booked'
                                      ? styles.chairBooked
                                      : styles.chairUnavailable
                                  }`}
                                  disabled={chair.status !== 'available'}
                                  onClick={() => this.setState({ selectedChairNumber: chair.chairNumber, bookingError: null })}
                                  title={`Chair ${chair.chairNumber} — ${chair.status}`}
                                >
                                  <span className={styles.chairIcon}>💺</span>
                                  <span className={styles.chairLabel}>{chair.chairNumber}</span>
                                  <span className={styles.chairStatus}>{chair.status === 'available' ? 'Free' : chair.status === 'booked' ? 'Taken' : 'N/A'}</span>
                                </button>
                              ))}
                            </div>
                            <div className={styles.chairLegend}>
                              <span className={styles.chairLegendItem}><span className={`${styles.chairLegendDot} ${styles.chairLegendAvailable}`} /> Available</span>
                              <span className={styles.chairLegendItem}><span className={`${styles.chairLegendDot} ${styles.chairLegendBooked}`} /> Booked</span>
                              <span className={styles.chairLegendItem}><span className={`${styles.chairLegendDot} ${styles.chairLegendUnavailable}`} /> Unavailable</span>
                            </div>
                            {this.state.selectedChairNumber && (
                              <small className={styles.formHint}>
                                You selected Chair {this.state.selectedChairNumber}
                              </small>
                            )}
                          </>
                        ) : (
                          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            {this.state.isJoinMode ? 'No chairs available for this slot.' : 'Fill in the date, time, and duration above to see available chairs.'}
                          </p>
                        )}
                      </div>
                    )}

                    <div className={styles.formActions}>
                      <button 
                        type="button" 
                        onClick={this.handleCloseModal} 
                        className={styles.cancelButton}
                        disabled={this.state.isSubmittingBooking}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className={styles.submitButton}
                        disabled={this.state.isSubmittingBooking || (this.state.isJoinMode && !this.state.selectedChairNumber)}
                      >
                        {this.state.isSubmittingBooking
                          ? (this.state.isJoinMode ? 'Joining...' : this.state.branchTypeCode === 'grocery' ? 'Sending...' : 'Booking...')
                          : (this.state.isJoinMode ? 'Join Table' : this.state.branchTypeCode === 'grocery' ? 'Send shopping request' : `Book ${branchTypeTheme({ code: this.state.branchTypeCode }).spot}`)}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* Chat Box - Show after booking */}
        {this.state.showChatBox && this.state.activeBooking && this.state.activeBooking.id && !this.state.activeTableChat && (
          <BookingChat
            organizerName={this.state.organizer?.name || 'Organizer'}
            branchName={this.state.branch?.name || 'Branch'}
            branchId={this.state.branch?.id}
            bookingId={this.state.activeBooking.id}
            onClose={this.handleCloseChat}
          />
        )}

        {/* Table Chat Box - Show for tables where isTableYours is true */}
        {this.state.showChatBox && this.state.activeTableChat && this.state.activeTableBookingId && !this.state.openChatBookingId && (
          <BookingChat
            organizerName={this.state.organizer?.name || 'Organizer'}
            branchName={this.state.branch?.name || 'Branch'}
            branchId={this.state.branch?.id}
            bookingId={this.state.activeTableBookingId}
            onClose={this.handleCloseTableChat}
          />
        )}

        {/* Chat from URL (FCM notification: .../tables?openChat=bookingId) */}
        {this.state.openChatBookingId && (
          <BookingChat
            organizerName={this.state.organizer?.name || 'Organizer'}
            branchName={this.state.branch?.name || 'Branch'}
            branchId={this.state.branch?.id}
            bookingId={this.state.openChatBookingId}
            onClose={this.handleCloseOpenChatFromUrl}
          />
        )}

        {/* Table Group Chat (organizer + all checked-in customers at the table) */}
        {this.state.showTableGroupChat && this.state.tableGroupChatBookingId && (
          <TableGroupChat
            bookingId={this.state.tableGroupChatBookingId}
            tableName={this.state.tableGroupChatTableName || undefined}
            branchId={this.props.params?.branchId || this.state.branch?.id}
            onClose={this.handleCloseTableGroupChat}
          />
        )}

        {/* Call staff — the software table-call button (routes to the table's assigned staff) */}
        {this.state.showCallStaff && this.state.callStaffBookingId && (
          <CallStaff
            bookingId={this.state.callStaffBookingId}
            tableName={this.state.callStaffTableName || undefined}
            branchType={{ code: this.state.branchTypeCode || undefined }}
            branchId={this.props.params?.branchId || this.state.branch?.id}
            onClose={this.handleCloseCallStaff}
          />
        )}

        {/* View bill / Pay — itemized tab + tab payment (Razorpay or mock, or pay at counter) */}
        {this.state.showBill && this.state.billBookingId && (
          <BillModal
            bookingId={this.state.billBookingId}
            tableName={this.state.billTableName || undefined}
            branchCurrency={this.state.branchCurrency || undefined}
            onClose={this.handleCloseBill}
          />
        )}

        {/* Order / Menu — the real menu-from-inventory order sheet (same as QR guests) */}
        {this.state.showOrder && this.state.orderBookingId && (
          <TableServiceOrderModal
            branchId={this.props.params?.branchId || this.state.branch?.id || ''}
            bookingId={this.state.orderBookingId}
            tableName={this.state.orderTableName || undefined}
            onClose={this.handleCloseOrder}
          />
        )}

        {/* Live score — game/sports venue scoreboard (billiards/chess/carrom/foosball/darts…) */}
        {this.state.showGameScore && this.state.gameScoreBookingId && this.state.gameScoreTableId && (
          <GameScorePanel
            asModal
            branchId={this.props.params?.branchId || this.state.branch?.id || ''}
            bookingId={this.state.gameScoreBookingId}
            tableId={this.state.gameScoreTableId}
            tableName={this.state.gameScoreTableName || undefined}
            onClose={this.handleCloseGameScore}
          />
        )}

        {/* Shopping request chat (grocery) */}
        {this.state.showShoppingRequestChat && this.state.myShoppingRequest && this.state.branch && (
          <ShoppingRequestChat
            requestId={this.state.myShoppingRequest.id}
            branchName={this.state.branch.name}
            shopName={this.state.organizer?.name || this.state.branch.name}
            onClose={this.handleCloseShoppingRequestChat}
            initialRequestStatus={this.state.myShoppingRequest.status}
          />
        )}

        {/* Booking Details Modal */}
        {this.state.showBookingDetailsModal && this.state.selectedTableForDetails && (
          <div className={styles.modalOverlay} onClick={this.handleCloseBookingDetailsModal}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>Booking Details - {(this.state.selectedTableForDetails as any).tableName || (this.state.selectedTableForDetails as any).tableNumber || `Table ${this.state.selectedTableForDetails.number}`}</h2>
                <button className={styles.modalClose} onClick={this.handleCloseBookingDetailsModal}>×</button>
              </div>

              {(this.state.selectedTableForDetails as any).imageUrl && (
                <img src={(this.state.selectedTableForDetails as any).imageUrl} alt="Where this table is" style={{ width: '100%', height: 170, objectFit: 'cover', borderRadius: 12, margin: '0 0 12px' }} />
              )}

              <div className={styles.bookingDetailsContent}>
                {this.state.isLoadingBookings ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading booking details...</p>
                  </div>
                ) : this.state.bookingsError ? (
                  <div className={styles.errorMessage}>
                    <span>⚠️ {this.state.bookingsError}</span>
                  </div>
                ) : this.state.tableBookings.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No booking details found for this table.</p>
                  </div>
                ) : (
                  <div className={styles.bookingsList}>
                    {this.state.tableBookings.map((booking: any) => (
                      <div key={booking.id} className={styles.bookingCard}>
                        <div className={styles.bookingCardHeader}>
                          <div className={styles.bookingStatus}>
                            <span className={`${styles.statusBadge} ${styles[`status${this.formatStatus(booking.status)}`]}`}>
                              {this.formatStatus(booking.status)}
                            </span>
                            {booking.paymentStatus && (
                              <span className={`${styles.paymentBadge} ${styles[`payment${this.formatPaymentStatus(booking.paymentStatus)}`]}`}>
                                {this.formatPaymentStatus(booking.paymentStatus)}
                              </span>
                            )}
                          </div>
                          <div className={styles.bookingDate}>
                            {this.formatDateTime(booking.bookingDate, booking.startTime)}
                          </div>
                        </div>

                        <div className={styles.bookingDetails}>
                          <div className={styles.bookingDetailRow}>
                            <span className={styles.detailLabel}>Date:</span>
                            <span className={styles.detailValue}>{booking.bookingDate || 'N/A'}</span>
                          </div>
                          <div className={styles.bookingDetailRow}>
                            <span className={styles.detailLabel}>Time:</span>
                            <span className={styles.detailValue}>
                              {booking.startTime ? booking.startTime.substring(0, 5) : 'N/A'} - {booking.endTime ? booking.endTime.substring(0, 5) : 'N/A'}
                            </span>
                          </div>
                          <div className={styles.bookingDetailRow}>
                            <span className={styles.detailLabel}>Duration:</span>
                            <span className={styles.detailValue}>{booking.durationMinutes || 0} minutes</span>
                          </div>
                          <div className={styles.bookingDetailRow}>
                            <span className={styles.detailLabel}>Guests:</span>
                            <span className={styles.detailValue}>{booking.numberOfCustomers || 0}</span>
                          </div>
                          {(booking.chairNumber || booking.chair_number) && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Chair:</span>
                              <span className={styles.detailValue}>💺 Chair {booking.chairNumber || booking.chair_number}</span>
                            </div>
                          )}
                          {Number(booking.paymentAmount) > 0 && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Amount:</span>
                              <span className={styles.detailValue}>₹{Number(booking.paymentAmount).toFixed(2)}</span>
                            </div>
                          )}
                          {booking.paymentMethod && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Payment Method:</span>
                              <span className={styles.detailValue}>{booking.paymentMethod}</span>
                            </div>
                          )}
                          {booking.specialRequests && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Special Requests:</span>
                              <span className={styles.detailValue}>{booking.specialRequests}</span>
                            </div>
                          )}
                          {booking.confirmedComments && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Organizer Comments:</span>
                              <span className={styles.detailValue}>{booking.confirmedComments}</span>
                            </div>
                          )}
                          {booking.checkinComments && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Check-in Comments:</span>
                              <span className={styles.detailValue}>{booking.checkinComments}</span>
                            </div>
                          )}
                          {booking.checkoutComments && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Check-out Comments:</span>
                              <span className={styles.detailValue}>{booking.checkoutComments}</span>
                            </div>
                          )}
                          {booking.rejectComments && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Rejection Reason:</span>
                              <span className={styles.detailValue}>{booking.rejectComments}</span>
                            </div>
                          )}
                          {booking.checkedIn && booking.checkedInAt && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Checked In:</span>
                              <span className={styles.detailValue}>
                                {new Date(booking.checkedInAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {booking.checkedOutAt && (
                            <div className={styles.bookingDetailRow}>
                              <span className={styles.detailLabel}>Checked Out:</span>
                              <span className={styles.detailValue}>
                                {new Date(booking.checkedOutAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.modalActions}>
                <button 
                  className={styles.closeButton}
                  onClick={this.handleCloseBookingDetailsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Live Song Request Modal */}
        {this.state.showLiveSongModal && (this.state.branch?.id || this.props.params?.branchId) && (
          <div
            className={`${styles.modalOverlay} ${styles.liveSongModalOverlay}`}
            onClick={() => this.setState({ showLiveSongModal: false })}
          >
            <div className={`${styles.modalContent} ${styles.liveSongModalContent}`} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>🎵 Live Song Request</h2>
                <p className={styles.modalSubtitle}>
                  Request a song for the live performance. You need an arrived table booking to submit.
                </p>
                <button
                  className={styles.modalClose}
                  onClick={() => this.setState({ showLiveSongModal: false })}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className={styles.liveSongModalTabs}>
                <button
                  type="button"
                  className={`${styles.liveSongModalTab} ${this.state.liveSongModalTab === 'request' ? styles.liveSongModalTabActive : ''}`}
                  onClick={() => this.setState({ liveSongModalTab: 'request' })}
                >
                  Request a song
                </button>
                <button
                  type="button"
                  className={`${styles.liveSongModalTab} ${this.state.liveSongModalTab === 'all' ? styles.liveSongModalTabActive : ''}`}
                  onClick={() => this.setState({ liveSongModalTab: 'all' })}
                >
                  All requests
                </button>
              </div>
              <div className={styles.liveSongModalBody}>
                <div
                  className={`${styles.liveSongModalTabPane} ${this.state.liveSongModalTab === 'request' ? styles.liveSongModalTabPaneActive : ''}`}
                  aria-hidden={this.state.liveSongModalTab !== 'request'}
                >
                  <LiveSongRequestPanel
                    branchId={this.state.branch?.id || this.props.params?.branchId || ''}
                    inModal
                  />
                </div>
                <div
                  className={`${styles.liveSongModalTabPane} ${this.state.liveSongModalTab === 'all' ? styles.liveSongModalTabPaneActive : ''}`}
                  aria-hidden={this.state.liveSongModalTab !== 'all'}
                >
                  <LiveSongAllRequests branchId={this.state.branch?.id || this.props.params?.branchId || ''} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Requested Songs Modal */}
        {this.state.showRequestedSongsModal && (this.state.branch?.id || this.props.params?.branchId) && (
          <div
            className={styles.modalOverlay}
            onClick={() => this.setState({ showRequestedSongsModal: false })}
          >
            <div className={`${styles.modalContent} ${styles.liveSongModalContent}`} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2>📋 Requested Songs</h2>
                <p className={styles.modalSubtitle}>
                  Your song requests for this branch. Expand to view details and add comments.
                </p>
                <button
                  className={styles.modalClose}
                  onClick={() => this.setState({ showRequestedSongsModal: false })}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className={styles.liveSongModalBody}>
                <LiveSongRequestList
                  branchId={this.state.branch?.id || this.props.params?.branchId || ''}
                  onRequestSongClick={() => this.setState({ showRequestedSongsModal: false, showLiveSongModal: true })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            position: 'fixed',
            top: '100px',
            left: '20px',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 99999,
            maxWidth: '300px',
            display: 'none',
          }}>
            <div>showChatBox: {String(this.state.showChatBox)}</div>
            <div>activeBooking: {this.state.activeBooking ? 'exists' : 'null'}</div>
            <div>bookingId: {this.state.activeBooking?.id || 'N/A'}</div>
            <div>organizer: {this.state.organizer?.name || 'N/A'}</div>
            <div>branch: {this.state.branch?.name || 'N/A'}</div>
          </div>
        )}

        <Footer />
      </>
    );
  }
}

export default withNavigation(TableSelection);

